import type * as API from '@sosocio/creator-types/api';
import type {
	AjaxOptions,
	FontModel,
	FullOfferingModel,
	FullOfferingModels,
	FullOfferingOptionIntersectModel,
	FullOfferingOptionModel,
	FullOfferingOptionValueIntersectModel,
	FullOfferingOptionValueIntersectModels,
	FullOfferingOptionValueVirtualIntersectModel,
	FullOfferingOptionVirtualIntersectModel,
	OfferingFrameModel,
	PartialOfferingModel,
	PartialOfferingOptionIntersectModel,
	PartialOfferingOptionModel,
	PartialOfferingOptionValueIntersectModel,
	StartAppDataToExclude,
} from '@sosocio/creator-types/app';
import type * as DB from '@sosocio/creator-types/database';
import type {
	MoodSceneModel,
	ProductSettings,
} from '@sosocio/creator-types/project';
import type { AxiosRequestConfig } from 'axios';
import ButtonComponent from 'components/button';
import ajax from 'controllers/ajax';
import merge from 'deepmerge';
import { applyBrandStyles } from 'design-system/apply-brand-styles';
import i18next, { i18n } from 'i18next';
import { COLOR_FULL } from 'settings/offerings';
import {
	AppDataModule,
	AppStateModule,
	ChannelsModule,
	ConfigModule,
	FontModule,
	ProductStateModule,
	UIModule,
	UserModule,
} from 'store';
import { componentSlot } from 'store/ui';
import { applyPrintColorsToOfferingModel } from 'tools/project/offering';
import _ from 'underscore';
import reportError from 'utils/report-error';
import { reactive } from 'vue';
import {
	Action,
	Module,
	Mutation,
	VuexModule,
} from 'vuex-module-decorators';
import { findOfferingEditorMessages as findAllOfferingEditorMessages } from './editor-messages';
import { mergeAndMergeByKey } from './merge-by-key';
import {
	findOfferingPageAssetModel,
	resolveOfferingPageAssetSource,
} from './offering-page-assets';
import {
	findOfferingPageSpecification,
	mergeOfferingPageSpecifications,
} from './offering-page-specifications';
import { buildStartAppDataUrl } from './start-url';

type OfferingModelSearchProps = PublicOptionalProps<Pick<DB.OfferingModel, 'externalid' | 'flexgroupid' | 'groupid' | 'id' | 'instock' | 'pdpid' | 'typeid' | 'variantid' | 'virtual'>>;
type OfferingPageAssetPayload = {
	offering: DB.OfferingModel;
	pageIndex?: number;
};
type ProductCategoriesDataFetchAttributes = Pick<DB.UserModel, 'countryid' | 'currency' | 'language'>;

function getProductCategoriesDataFetchKey({
	countryid,
	currency,
	language,
}: ProductCategoriesDataFetchAttributes): string {
	return [
		countryid ?? '',
		currency ?? '',
		language ?? '',
	].join('|');
}

function getCurrentProductCategoriesDataFetchKey(): string {
	return getProductCategoriesDataFetchKey({
		countryid: UserModule.countryid,
		currency: UserModule.currency,
		language: UserModule.language,
	});
}

/*
 * Fetch tracking lives outside the Vuex state on purpose: promises are not
 * serializable state, and vuex-module-decorators only allows assignment to
 * state fields inside mutations (a direct assignment from an action throws
 * at runtime). The module's reactive `productCategoriesDataFetchesPending`
 * counter mirrors the map size for UI consumers.
 */
const productCategoriesDataFetchPromises = new Map<string, Promise<void>>();
let completedProductCategoriesDataFetchKey: string | null = null;

function localizeOfferingEditorMessage(
	editorMessage: API.OfferingDataEditorMessageModel,
): API.OfferingDataEditorMessageModel {
	const translationKey = `editorMessages:${editorMessage.id}`;
	const i18nextModule = globalThis.window?.App?.router?.$i18next ?? i18next;

	return {
		...editorMessage,
		message: i18nextModule.exists(translationKey)
			? i18nextModule.t(translationKey)
			: editorMessage.message,
	};
}

/**
 * Resolves the correct offering option value to be used in the editor, this is needed to apply
 * the correct print color value when shops indicate to limit them, this is done by providing the
 * values of type print color that belong to the same original offering (defined by the original
 * group id and original type id) as the offering being configured.
 *
 * @param offeringOptionValue {@link DB.OfferingOptionValueModel} to resolve.
 * @param offeringOption {@link DB.OfferingOptionModel} that belongs to the offering option value.
 * @param offeringOptionValueOfferingLink {@link DB.OfferingOptionValueOfferingModel} that belongs to the offering option value.
 * @param productSettings {@link ProductSettings} of the product being configured, needed to get the print color settings and the original offering identifiers.
 * @param productSettings.originalGroupId Original group id of the offering to which the offering option value being resolved belongs, needed to identify if the offering option value belongs to the original offering.
 * @param productSettings.originalTypeId Original type id of the offering to which the offering option value being resolved belongs, needed to identify if the offering option value belongs to the original offering.
 * @param productSettings.printColors Print color settings of the product, needed to resolve the correct print color value to be used in the editor.
 * @returns {@link DB.OfferingOptionValueModel} resolved offering option value.
 */
export function resolveOfferingOptionValue(
	offeringOptionValue: DB.OfferingOptionValueModel,
	offeringOption: OptionalExceptFor<DB.OfferingOptionModel, 'tag'>,
	offeringOptionValueOfferingLink: OptionalExceptFor<DB.OfferingOptionValueOfferingModel, 'offeringid'>,
	productSettings?: ProductSettings,
): DB.OfferingOptionValueModel {
	const {
		originalGroupId,
		originalTypeId,
		printColors,
	} = (
		productSettings
		?? {}
	);

	if (
		!printColors
		|| !originalGroupId
		|| !originalTypeId
		|| offeringOption.tag !== 'print-color'
	) {
		return offeringOptionValue;
	}

	const offeringModelFromLink = AppDataModule.getOffering(offeringOptionValueOfferingLink.offeringid);

	if (
		!offeringModelFromLink
		|| offeringModelFromLink.groupid !== originalGroupId
		|| offeringModelFromLink.typeid !== originalTypeId
	) {
		return offeringOptionValue;
	}

	if (printColors === 'fc') {
		return {
			...offeringOptionValue,
			value: COLOR_FULL,
		};
	}

	if (
		typeof offeringOptionValue.value === 'number'
		&& offeringOptionValue.value <= printColors
	) {
		return offeringOptionValue;
	}

	return {
		...offeringOptionValue,
		value: printColors,
	};
}

const pendingOfferingRequests = new Map<string, Promise<void>>();

function getOfferingSearchPropsKey(searchProps: OfferingModelSearchProps): string {
	return Object.entries(searchProps)
		.filter(([, value]) => typeof value !== 'undefined')
		.sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
		.map(([
			key,
			value,
		]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
		.join('&');
}

/**
 * Coalesce concurrent id-only offering fetches into one batched request per
 * microtask. Shelf item rendering can otherwise call `getOfferingName` for N
 * missing offering ids in the same tick and produce N `/offeringsdata?where=`
 * requests.
 */
const pendingIdBatchQueue = new Set<number>();
let pendingIdBatchPromise: Promise<void> | null = null;

function queueOfferingIdBatch(id: number): Promise<void> {
	pendingIdBatchQueue.add(id);

	if (!pendingIdBatchPromise) {
		pendingIdBatchPromise = Promise.resolve().then(() => {
			const offeringIds = [...pendingIdBatchQueue];
			pendingIdBatchQueue.clear();
			pendingIdBatchPromise = null;

			const request = AppDataModule.fetchOfferingsData({
				offeringIds,
			}).finally(() => {
				for (const batchedId of offeringIds) {
					const key = getOfferingSearchPropsKey({
						id: batchedId,
					});

					if (pendingOfferingRequests.get(key) === request) {
						pendingOfferingRequests.delete(key);
					}
				}
			});

			for (const batchedId of offeringIds) {
				pendingOfferingRequests.set(
					getOfferingSearchPropsKey({
						id: batchedId,
					}),
					request,
				);
			}

			return request;
		});
	}

	return pendingIdBatchPromise;
}

function isIdOnlySearch(searchProps: OfferingModelSearchProps): searchProps is { id: number } {
	const keys = Object.keys(searchProps);
	return (
		keys.length === 1
		&& keys[0] === 'id'
		&& typeof searchProps.id === 'number'
	);
}

function findAndSetOffering(searchProps: OfferingModelSearchProps): Promise<void> {
	if (isIdOnlySearch(searchProps)) {
		const pendingRequest = pendingOfferingRequests.get(getOfferingSearchPropsKey(searchProps));

		if (pendingRequest) {
			return pendingRequest;
		}

		return queueOfferingIdBatch(searchProps.id);
	}

	const searchPropsKey = getOfferingSearchPropsKey(searchProps);
	const pendingRequest = pendingOfferingRequests.get(searchPropsKey);

	if (pendingRequest) {
		return pendingRequest;
	}

	const request = AppDataModule.fetchOfferingsData({
		searchProps,
	}).finally(() => {
		pendingOfferingRequests.delete(searchPropsKey);
	});

	pendingOfferingRequests.set(
		searchPropsKey,
		request,
	);

	return request;
}

function getOfferingPageAssetPayloadPageIndex(payload: OfferingPageAssetPayload): number {
	return payload.pageIndex ?? 0;
}

function resolveOfferingMaskAssetSourceFromPayload(
	offeringmaskimages: ReadonlyArray<DB.OfferingMaskImageModel>,
	payload: OfferingPageAssetPayload,
) {
	return resolveOfferingMaskAssetSource(
		offeringmaskimages,
		payload.offering,
		getOfferingPageAssetPayloadPageIndex(payload),
	);
}

function resolveOfferingOverlayAssetSourceFromPayload(
	offeringoverlayimages: ReadonlyArray<DB.OfferingOverlayImageModel>,
	payload: OfferingPageAssetPayload,
) {
	return resolveOfferingOverlayAssetSource(
		offeringoverlayimages,
		payload.offering,
		getOfferingPageAssetPayloadPageIndex(payload),
	);
}

function loadOfferingFrameImage(imageModel: DB.OfferingFrameImageModel): Promise<void> {
	const image = new Image();
	image.crossOrigin = 'Anonymous';

	return new Promise<void>((resolve, reject) => {
		image.onload = () => {
			AppDataModule.setOfferingFrameImageDimensions({
				id: imageModel.id,
				height: image.naturalHeight,
				width: image.naturalWidth,
			});
			resolve();
		};
		image.onerror = reject;
		image.src = `${imageModel.url}?noCorsHeader`;
	});
}

function resolveOfferingMaskAssetSource(
	offeringmaskimages: ReadonlyArray<DB.OfferingMaskImageModel>,
	offering: DB.OfferingModel,
	pageIndex = 0,
) {
	return resolveOfferingPageAssetSource({
		assetKind: 'mask',
		fallbackUrl: offering.mask,
		models: offeringmaskimages,
		offeringId: offering.id,
		pageIndex,
	});
}

function resolveOfferingOverlayAssetSource(
	offeringoverlayimages: ReadonlyArray<DB.OfferingOverlayImageModel>,
	offering: DB.OfferingModel,
	pageIndex = 0,
) {
	return resolveOfferingPageAssetSource({
		assetKind: 'overlay',
		fallbackUrl: offering.overlay,
		models: offeringoverlayimages,
		offeringId: offering.id,
		pageIndex,
	});
}

function mergeTranslationBundles(
	oldBundle: Record<any, any>,
	newBundle: Record<any, any>,
): Record<any, any> {
	const newBundleToReturn = {
		...oldBundle,
	};
	const newBundleKeys = Object.keys(newBundle);

	for (const newBundleKey of newBundleKeys) {
		if (oldBundle[newBundleKey]) {
			if (
				typeof oldBundle[newBundleKey] === 'object'
				&& oldBundle[newBundleKey] !== null
				&& typeof newBundle[newBundleKey] === 'object'
				&& newBundle[newBundleKey] !== null
			) {
				newBundleToReturn[newBundleKey] = mergeTranslationBundles(
					newBundleToReturn[newBundleKey],
					newBundle[newBundleKey],
				);
			} else if (
				!oldBundle[newBundleKey]
				&& newBundle[newBundleKey]
			) {
				newBundleToReturn[newBundleKey] = newBundle[newBundleKey];
			}
		} else {
			newBundleToReturn[newBundleKey] = newBundle[newBundleKey];
		}
	}

	return newBundleToReturn;
}

/**
 * Normalizes offering fields that are expected to be safe at runtime.
 *
 * @param offering {@link DB.OfferingModel} to normalize.
 * @returns Normalized {@link DB.OfferingModel} with a safe config dpi fallback.
 */
function normalizeOfferingModel(offering: DB.OfferingModel): DB.OfferingModel {
	const configDpi = Number(offering.configdpi);
	const qualityDpi = Number(offering.qualitydpi);
	const minimumDpi = Number(offering.minimumdpi);
	let resolvedConfigDpi = configDpi;
	let normalizedSpine: DB.SpineModel[] | undefined;

	if (
		!Number.isFinite(resolvedConfigDpi)
		|| resolvedConfigDpi <= 0
	) {
		if (
			Number.isFinite(qualityDpi)
			&& qualityDpi > 0
		) {
			resolvedConfigDpi = qualityDpi;
		} else if (
			Number.isFinite(minimumDpi)
			&& minimumDpi > 0
		) {
			resolvedConfigDpi = minimumDpi;
		} else {
			resolvedConfigDpi = 300;
		}
	}

	if (typeof offering.spine === 'string') {
		try {
			normalizedSpine = JSON.parse(offering.spine);
		} catch {
			normalizedSpine = undefined;

			reportError(
				new Error(`Failed to parse spine for offering ${offering.id}: ${offering.spine}`),
				'error',
			);
		}
	} else if (Array.isArray(offering.spine)) {
		normalizedSpine = offering.spine;
	}

	return {
		...offering,
		configdpi: resolvedConfigDpi,
		externalid: (
			offering.externalid
				? offering.externalid.toString()
				: ''
		),
		spine: normalizedSpine,
	};
}

@Module({
	namespaced: true,
	name: 'appdata',
})
export default class AppData extends VuexModule {
	badges = reactive<DB.BadgeModel[]>([]);

	badgeimages = reactive<DB.BadgeImageModel[]>([]);

	badgeofferinglinks = reactive<DB.BadgeOfferingModel[]>([]);

	badgepdplinks = reactive<DB.BadgePDPModel[]>([]);

	badgeproductcategorylinks = reactive<DB.BadgeProductCategoryModel[]>([]);

	bulks = reactive<DB.BulkModel[]>([]);

	bulkproducttypes = reactive<DB.BulkProductTypeModel[]>([]);

	bulkquantities = reactive<DB.BulkQuantityModel[]>([]);

	buckets = reactive<DB.BucketModel[]>([]);

	countries = reactive<DB.CountryModel[]>([]);

	currencies = reactive<DB.CurrencyModel[]>([]);

	fonts = reactive<FontModel[]>([]);

	flexgroups = reactive<API.FlexGroupModel[]>([]);

	handling = reactive<DB.HandlingModel[]>([]);

	hyperlinks = reactive<DB.HyperlinkModel[]>([]);

	languages = reactive<DB.LanguageModel[]>([]);

	models2d = reactive<DB.Model2DModel[]>([]);

	models3d = reactive<DB.Model3DModel[]>([]);

	private _moodscenesimages = reactive<Record<DB.ProductModel['id'], MoodSceneModel>>({});

	offeringframes = reactive<DB.OfferingFrameModel[]>([]);

	offeringframeimages = reactive<DB.OfferingFrameImageModel[]>([]);

	offeringmaskimages = reactive<DB.OfferingMaskImageModel[]>([]);

	offeringoverlayimages = reactive<DB.OfferingOverlayImageModel[]>([]);

	offeringframetemplates = reactive<DB.OfferingFrameTemplateModel[]>([]);

	_offeringframesimages = reactive<Record<DB.OfferingFrameImageModel['id'], HTMLImageElement>>({});

	_offeringmasksimages = reactive<Record<string, HTMLImageElement>>({});

	_offeringoverlaysimages = reactive<Record<string, HTMLImageElement>>({});

	private _offeringsDataMergeQueue = reactive(Promise.resolve());

	editorMessages = reactive<API.OfferingDataEditorMessageModel[]>([]);

	offerings = reactive<DB.OfferingModel[]>([]);

	offeringofferingframelinks = reactive<DB.OfferingOfferingFrameLinkModel[]>([]);

	offeringoptions = reactive<DB.OfferingOptionModel[]>([]);

	offeringoptionvalues = reactive<DB.OfferingOptionValueModel[]>([]);

	offeringoptionvalueofferinglinks = reactive<DB.OfferingOptionValueOfferingModel[]>([]);

	offeringpages = reactive<API.OfferingDataPageSpecification[]>([]);

	pdps = reactive<API.PDPModel[]>([]);

	pdpfilters = reactive<DB.PDPFilterModel[]>([]);

	pdpfiltervalues = reactive<DB.PDPFilterValueModel[]>([]);

	pdpfiltervalueofferinglinks = reactive<DB.PDPFilterValueOfferingModel[]>([]);

	pdpimages = reactive<DB.PDPImageModel[]>([]);

	pricing = reactive<DB.PricingModel[]>([]);

	productcategories = reactive<API.ProductCategoryModel[]>([]);

	productCategoriesDataFetchesPending = 0;

	productgroups = reactive<DB.ProductGroupModel[]>([]);

	regioncurrencylinks = reactive<DB.RegionCurrencyModel[]>([]);

	regionofferinglinks = reactive<DB.RegionOfferingModel[]>([]);

	regions = reactive<DB.RegionModel[]>([]);

	stateprovs = reactive<DB.StateProvModel[]>([]);

	upsells = reactive<DB.UpsellModel[]>([]);

	private get collectionUrl(): string {
		return buildStartAppDataUrl({
			appDataVersion: window.appDataVersion,
			appUrl: window.glAppUrl,
			domain: window.location.hostname,
			stack: window.glStack,
		});
	}

	get excludeEmptyPages(): (offeringid: number) => boolean {
		return (offeringid) => {
			const offeringModel = this.getOffering(offeringid);

			/*
			 * Note: Upon migration to JSON schema this check can be performed directly on the property
			 * in the offering data, but for now we need to compile the boolean value like this
			 */
			if (
				offeringModel?.type === 'logo'
				&& offeringModel.maxpages > offeringModel.minprintpages
			) {
				return true;
			}

			return false;
		};
	}

	get getAvailableOfferings() {
		const items: DB.OfferingModel[] = [];
		const userCountryId = this.context.rootState.user.countryid;
		const countryModel = (
			userCountryId
				? this.countries.find((country) => country.id === userCountryId)
				: undefined
		);

		if (countryModel) {
			this.offerings.forEach((offeringModel) => {
				const isAvailable = this.regionofferinglinks
					.find((regionofferinglink) => (
						regionofferinglink.regionid === countryModel.regionid
						&& regionofferinglink.offeringid === offeringModel.id
					));

				if (isAvailable) {
					items.push(offeringModel);
				}
			});
		}

		return items;
	}

	get getBucket() {
		return (id: string) => _.findWhere(
			this.buckets,
			{
				id,
			},
		);
	}

	get getBulk() {
		return (id: number) => _.findWhere(
			this.bulks,
			{
				id,
			},
		);
	}

	get getCountry() {
		return (id: number) => _.findWhere(
			this.countries,
			{
				id,
			},
		);
	}

	get getCountryName() {
		return (id: number) => {
			const countryModel = _.findWhere(
				this.countries,
				{
					id,
				},
			);

			if (countryModel) {
				let i18nextModule: i18n;

				if (window.App?.router.$i18next) {
					i18nextModule = window.App.router.$i18next;
				} else {
					i18nextModule = i18next;
				}

				return i18nextModule.exists(`countries.${countryModel.iso}`)
					? i18nextModule.t(`countries.${countryModel.iso}`)
					: countryModel.name;
			}

			return '';
		};
	}

	get getCurrency() {
		return (id: string): DB.CurrencyModel | undefined => this.currencies.find((currency) => currency.id === id);
	}

	get getCurrencyByCountryId() {
		return (countryId: number) => {
			const models: DB.CurrencyModel[] = [];
			const countryModel = this.getCountry(countryId);

			if (countryModel) {
				const { regionid } = countryModel;

				_.each(
					this.currencies,
					(currencyModel) => {
						if (this.findRegionCurrencyLinkWhere({
							regionid,
							currencyid: currencyModel.id,
						})) {
							models.push(currencyModel);
						}
					},
				);
			}

			return models;
		};
	}

	get getCurrencyByISO() {
		return (iso: DB.CurrencyModel['iso']): DB.CurrencyModel | undefined => this.currencies.find((currency) => currency.iso === iso);
	}

	public get getOffering(): (offeringid: number) => DB.OfferingModel | undefined {
		return (offeringid) => {
			const offeringFound = this.offerings.find((offering) => offering.id === offeringid);

			if (offeringFound) {
				return applyPrintColorsToOfferingModel(
					offeringFound,
					ProductStateModule.getProductSettings,
				);
			}

			findAndSetOffering({
				id: offeringid,
			});

			return undefined;
		};
	}

	get getOfferingDescription() {
		return (
			offeringId: DB.OfferingModel['id'],
		): string => {
			let description = '';

			const lineOne = window.App.router.$t(
				`offerings:${offeringId}.lines.0`,
				'',
			);

			if (lineOne?.length) {
				description += lineOne;
			}

			const lineTwo = window.App.router.$t(
				`offerings:${offeringId}.lines.1`,
				'',
			);

			if (lineTwo?.length) {
				if (description.length) {
					description += ' | ';
				}

				description += lineTwo;
			}

			return description;
		};
	}

	public get getOfferingName(): (
		offeringId: DB.OfferingModel['id'],
		addDescription?: boolean,
	) => string {
		return (
			offeringId,
			addDescription,
		) => {
			this.findAndGetOfferingWhere({
				id: offeringId,
			});
			let name = window.App.router.$t(
				`offerings:${offeringId}.name`,
				'',
			);

			if (addDescription) {
				const description = this.getOfferingDescription(offeringId);

				if (description.length) {
					name += ` | ${description}`;
				}
			}

			return name;
		};
	}

	get getOfferingOptionModels() {
		return (
			offeringid: DB.OfferingModel['id'],
			regionid?: DB.RegionModel['id'],
		) => {
			const offeringOptionModels: DB.OfferingOptionModel[] = [];
			this
				.getOfferingOptionValueModels(
					offeringid,
					regionid,
				)
				.forEach((offeringOptionValueModel) => {
					const offeringOptionModel = this.offeringoptions
						.find((offeringoption) => offeringoption.id === offeringOptionValueModel.offeringoptionid);

					if (
						offeringOptionModel
						&& !offeringOptionModels.includes(offeringOptionModel)
					) {
						offeringOptionModels.push(offeringOptionModel);
					}
				});

			return offeringOptionModels.sort((a, b) => a.serialnumber - b.serialnumber);
		};
	}

	/**
	 * Given a set of offering models or offering ids, return each offering model with its
	 * corresponding offering option models and their values.
	 * @param offeringIds - A list of offering ids.
	 * @param offeringModels - A list of offering models.
	 * @param regionid - The region id to filter the offerings by.
	 * @param filter - A function to filter the each offering model before pushing
	 * it to the final list of offering models.
	 * The filter must return either the full offering model (with the new desired
	 * structure/properties) or false to not include it in the final list.
	 * @param virtual - Whether to treat the offering models as virtual and add the
	 * offering model itself as an offering option.
	 * @returns {FullOfferingModel} - The final list of offering models filtered
	 * by regionid (if it was provided) with their corresponding offering
	 * option models and their values.
	 */
	public get getFullOfferingOptionModels(): (
		options: {
			offeringIds?: DB.OfferingModel['id'][];
			offeringModels?: DB.OfferingModel[];
			regionid?: DB.RegionModel['id'];
			filter?: (fullOfferingModel: FullOfferingModel) => FullOfferingModel | false;
			productSettings?: ProductSettings;
			virtual?: boolean;
		},
	) => FullOfferingModels {
		return ({
			offeringIds,
			offeringModels: offeringsModels,
			regionid,
			filter,
			productSettings,
			virtual,
		}) => {
			if (
				!offeringIds?.length
				&& !offeringsModels?.length
			) {
				return [];
			}

			const {
				originalGroupId,
				originalTypeId,
			} = (
				productSettings
				?? {}
			);
			/**
			 * Defines where the set of offering ids will be stored
			 */
			let finalOfferingIds: number[] = [];
			/**
			 * Defines where the set of offering models will be stored, the key
			 * of each offering model will be its own id for easy access.
			 */
			let finalOfferingModels: Record<number, DB.OfferingModel> = {};

			if (offeringIds) {
				/**
				 * Normalized offering ids from the offerings ids already provided
				 */
				finalOfferingIds = offeringIds;
				/**
				 * Normalized offering models from the offering ids, got from the
				 * `AppData.getOffering()` method
				 */
				finalOfferingModels = finalOfferingIds
					.reduce(
						(offerings, offeringid) => {
							const offeringModel = this.getOffering(offeringid);

							if (offeringModel) {
								offerings[offeringid] = offeringModel;
							}

							return offerings;
						},
						{} as Record<number, DB.OfferingModel>,
					);
			} else if (offeringsModels) {
				/**
				 * Normalized offering ids from the offering models
				 */
				finalOfferingIds = offeringsModels.map((offering) => (offering as DB.OfferingModel).id);
				/**
				 * Normalized offering models from the offering models already provided
				 */
				finalOfferingModels = offeringsModels.reduce(
					(offerings, offering) => {
						offerings[offering.id] = offering;

						return offerings;
					},
					{} as Record<number, DB.OfferingModel>,
				);
			}

			/**
			 * If a region id was provided, filter the offering ids by the region id
			 * by matching the region id and the offering id with the region offering links
			 */
			if (regionid) {
				finalOfferingIds = finalOfferingIds
					.filter((offeringid) => (
						this.regionofferinglinks
							.find((regionofferinglink) => (
								regionofferinglink.regionid === regionid
								&& regionofferinglink.offeringid === offeringid
							))
					));
			}

			/**
			 * If there are no offering ids available after the filter, return an empty list
			 */
			if (!finalOfferingIds.length) {
				return [];
			}

			if (
				typeof originalGroupId !== 'undefined'
				&& typeof originalTypeId !== 'undefined'
			) {
				const offeringModelFound = Object
					.values(finalOfferingModels)
					.find((offeringModel) => (
						offeringModel.groupid === originalGroupId
						&& offeringModel.typeid === originalTypeId
					));

				if (offeringModelFound) {
					finalOfferingModels[offeringModelFound.id] = applyPrintColorsToOfferingModel(
						finalOfferingModels[offeringModelFound.id],
						productSettings,
					);
				}
			}

			/**
			 * Iterate over the offering value offering links to return the final list of
			 * offering option models and their values, this model comprehens a match of
			 * an offering id and an offering option value id.
			 *
			 * This means a match of every offering option value with its corresponding
			 * offering id, for example: values ids (e.g. landscape, 60x40 and white frame)
			 * and the offering id that match all these values together.
			 */
			const fullOfferingModels = this.offeringoptionvalueofferinglinks
				.reduce(
					(fullOfferingWithOptions, offeringOptionValueOfferingLink) => {
						/**
						 * If the offering id from the link model is not in the list
						 * of offering ids provided, then we skip it
						 */
						if (finalOfferingIds.includes(offeringOptionValueOfferingLink.offeringid)) {
							/**
							 * Get the offering option value models by matching the offering
							 * option value id with the link model offering option value id,
							 * this model comprehends the match of an offering option value
							 * with its corresponding offering option id. It also provides
							 * the serialnumber (display order of the offering option value)
							 *
							 * This means a match of every offering option value with its
							 * corresponding offering option id, for example:
							 * values (e.g. landscape, portrait and square) and the offering
							 * option id that owns all these values.
							 */
							const offeringOptionValues = this.offeringoptionvalues
								.filter((offeringOptionValue) => (
									offeringOptionValue.id === offeringOptionValueOfferingLink.offeringoptionvalueid
								));

							if (offeringOptionValues.length) {
								/**
								 * List of each offering option value model with its corresponding
								 * offering option model, though the structure being returned
								 * is a list of offering option models, but each model has a
								 * `value` property that contains the offering option value.
								 *
								 * This is made this way since the hierarchy of the offerings are
								 * offering options containing offering option values.
								 */
								const offeringOptions = offeringOptionValues
									.reduce(
										(finalOfferingOptions, offeringOptionValue) => {
											/**
											 * Get the offering option model by matching the offering
											 * option id with the offering option value offering option id
											 */
											const offeringOption = this.offeringoptions
												.find((offeringOptionModel) => (
													offeringOptionModel.id === offeringOptionValue.offeringoptionid
												));

											if (offeringOption) {
												/**
												 * Add the offering option model with its corresponding
												 * offering option value model to the list of offering
												 * option models
												 */
												finalOfferingOptions.push({
													...offeringOption,
													value: resolveOfferingOptionValue(
														offeringOptionValue,
														offeringOption,
														offeringOptionValueOfferingLink,
														productSettings,
													),
												});
											}

											return finalOfferingOptions;
										},
										[] as FullOfferingOptionModel[],
									);

								/**
								 * Search for an existing full offering model with options
								 * in the working final list to return  by matching the
								 * offering id with the offering id from the offering option
								 * value offering link model
								 */
								let fullOfferingFound = fullOfferingWithOptions
									.find((fullOfferingWithOption) => (
										fullOfferingWithOption.id === offeringOptionValueOfferingLink.offeringid
									));
								/**
								 * Variable to store the filtered full offering model or in the case
								 * that the filter indicates that the full offering model should be
								 * removed, then it will be false
								 */
								let filteredFullOfferingModel: FullOfferingModel | undefined | false;

								/**
								 * If the full offering model with options is not found,
								 * then we create it by getting the offering model with the
								 * list of offering models from the beginning of the function
								 * using the offering id from the offering option value offering
								 * link model and adding an empty list of options, and then we
								 * add it to the working final list to return
								 */
								if (!fullOfferingFound) {
									fullOfferingFound = {
										...finalOfferingModels[offeringOptionValueOfferingLink.offeringid],
										options: [],
									};
									fullOfferingWithOptions.push(fullOfferingFound);
								}

								/**
								 * Search for a potential virtual offering option if the variantid option
								 * was provided
								 */
								const virtualOptionFound = !!(
									virtual
									&& fullOfferingFound.options.find((option) => option.variantid)
								);

								/**
								 * If the variantid option was provided and the virtual option was not
								 * found, then we add it to the full offering options
								 */
								if (
									virtual
									&& !virtualOptionFound
									&& !fullOfferingFound.options.find((option) => option.tag === 'size')
								) {
									fullOfferingFound.options.push({
										name: 'size',
										serialnumber: -1,
										tag: 'size',
										value: {
											offeringid: offeringOptionValueOfferingLink.offeringid,
											serialnumber: -1,
											tag: null,
											value: finalOfferingModels[offeringOptionValueOfferingLink.offeringid].description as string,
										},
										variantid: finalOfferingModels[offeringOptionValueOfferingLink.offeringid].variantid,
									});
								}

								if (virtual) {
									const realSizeOption = offeringOptions.find((option) => option.tag === 'size');

									if (realSizeOption) {
										const manualSizeOption = fullOfferingFound.options.find((option) => (
											option.tag === 'size'
											&& option.serialnumber === -1
										));

										if (manualSizeOption) {
											fullOfferingFound.options.splice(
												fullOfferingFound.options.indexOf(manualSizeOption),
												1,
											);
											realSizeOption.tag = 'size';
										}
									}
								}

								/**
								 * Add the full offering options to the full offering model
								 */
								fullOfferingFound.options.push(
									...offeringOptions,
								);

								/**
								 * Sort the full offering options by its serial number
								 */
								fullOfferingFound.options.sort((a, b) => a.serialnumber - b.serialnumber);

								/**
								 * If the filter param is provided, then we check if the
								 * filter passed or not by calling the filter function
								 * passing the full offering model with options
								 */
								if (filter) {
									filteredFullOfferingModel = filter(fullOfferingFound);
								}

								/**
								 * If the filter function returns a full offering model,
								 * then we replace the full offering model in the working
								 * final list with the filtered full offering model, otherwise
								 * if the filter function returns false, then we remove the
								 * full offering model from the working final list
								 */
								if (filteredFullOfferingModel) {
									fullOfferingWithOptions.splice(
										fullOfferingWithOptions.indexOf(fullOfferingFound),
										1,
										filteredFullOfferingModel,
									);
								} else if (filteredFullOfferingModel === false) {
									fullOfferingWithOptions.splice(
										fullOfferingWithOptions.indexOf(fullOfferingFound),
										1,
									);
								}
							}
						}

						return fullOfferingWithOptions;
					},
					[] as FullOfferingModels,
				);

			/**
			 * If the full offering models with options are not empty, then we
			 * return them, otherwise we return the list of offering models with
			 * an empty list of options
			 */
			if (fullOfferingModels.length > 0) {
				return fullOfferingModels;
			}

			/**
			 * Return the list of offering models with an empty list of options
			 * in the case that the full offering models with options are empty
			 * because it means that the offering models do not have offering
			 * option value offering link models associated with them
			 */
			return Object
				.values(finalOfferingModels)
				.map((offeringModel) => ({
					...offeringModel,
					options: [],
				}));
		};
	}

	public get getFullOfferingOptionIntersectModels(): (
		options: {
			offeringModels: FullOfferingModels,
			variantid?: number,
		},
	) => (FullOfferingOptionVirtualIntersectModel | FullOfferingOptionIntersectModel)[] {
		return ({
			offeringModels,
			variantid,
		}) => {
			const offeringOptionIntersectModels = offeringModels
				.reduce(
					(options, fullOfferingWithOptions) => {
						/**
						 * Check if the virtual mode is set and if the virtual option already exists
						 * in the options list
						 */
						const virtualOptionFound = (
							variantid
							&& options.find((optionModel) => (
								'variantid' in optionModel
								&& optionModel.variantid === fullOfferingWithOptions.variantid
							))
						);
						let virtualOption: FullOfferingOptionVirtualIntersectModel | undefined;

						if (
							variantid
							&& !virtualOptionFound
							&& variantid === fullOfferingWithOptions.variantid
							&& !options.find((optionModel) => optionModel.tag === 'size')
						) {
							virtualOption = {
								items: [],
								name: 'size',
								serialnumber: -1,
								tag: 'size',
								value: {
									offeringid: fullOfferingWithOptions.id,
									serialnumber: -1,
									tag: null,
									value: fullOfferingWithOptions.description as string,
								},
								variantid: fullOfferingWithOptions.variantid,
							};
							options.push(virtualOption);
						} else if (virtualOptionFound) {
							virtualOption = virtualOptionFound;
						}

						if (virtualOption) {
							const lastItem = virtualOption.items.at(-1);
							virtualOption.items.push({
								image: null,
								matchingOfferingIds: [],
								offeringid: fullOfferingWithOptions.id,
								serialnumber: lastItem ? lastItem.serialnumber + 1 : 1,
								tag: null,
								value: fullOfferingWithOptions.description as string,
							});
						}

						for (const fullOfferingOption of fullOfferingWithOptions.options) {
							/**
							 * Check if the current option in the iteration already exists in options list
							 */
							const optionFound = options
								.find((optionModel) => optionModel.id === fullOfferingOption.id);
							/**
							 * Create a new option in case it doesn't exist in the options list
							 */
							let option = {
								...fullOfferingOption,
								items: [],
							} as FullOfferingOptionVirtualIntersectModel | FullOfferingOptionIntersectModel;

							/**
							 * If the option already exists in the options list, use the existing one
							 * instead of using the new one created above
							 */
							if (optionFound) {
								option = optionFound;
							}

							/**
							 * Check if the current option value in the iteration already exists
							 * in the option values list, this is made because some options values
							 * can be repeated in different offerings, for example an orientation option
							 * value will be repeated for each size option value.
							 *
							 * E.g. being "landscape" the option value of "orientation":
							 * - A set with orientation "landscape" and size "30x20" will have the same
							 * option value "landscape" as in another set of orientation "landscape" and
							 * option value "45x30"
							 */
							const optionValueFound = (option.items as (FullOfferingOptionValueIntersectModel | FullOfferingOptionValueVirtualIntersectModel)[])
								.find((optionValueModel) => optionValueModel.id === fullOfferingOption.value.id);

							/**
							 * If the option value doesn't exist in the option values list, add it
							 * to the option values list
							 */
							if (!optionValueFound) {
								option.items.push({
									...fullOfferingOption.value as any,
									/**
									 * Add all the offering ids where the current option value is available,
									 * this is needed in a later point to filter the offering option values
									 * to show only the ones that are available for the current set of
									 * offering option values selected by the user.
									 *
									 * For example if the user selects the orientation "square", we only
									 * want to show the size option values that are available for the
									 * orientation "square"
									 */
									matchingOfferingIds: offeringModels
										.filter((fullOfferingModel) => {
											const optionValue = fullOfferingModel.options
												.find((optionModel) => optionModel.id === fullOfferingOption.id)
												?.value;

											if (!optionValue) {
												return false;
											}

											return optionValue.id === fullOfferingOption.value.id;
										})
										.map((fullOfferingModel) => fullOfferingModel.id),
								});
								/**
								 * Sort the option values by serial number, this is needed because
								 * the option values are not always returned in the correct order
								 */
								option.items.sort((a, b) => a.serialnumber - b.serialnumber);

								if (virtualOption) {
									const virtualOptionItem = virtualOption.items[virtualOption.items.length - 1];
									virtualOptionItem.matchingOfferingIds = Array.from(
										new Set(
											options
												.filter((currentOption) => 'variantid' in currentOption)
												.reduce(
													(matchingOfferingIds, currentOption) => {
														const currentOptionItems = currentOption.items as FullOfferingOptionValueVirtualIntersectModel[];

														return [
															...matchingOfferingIds,
															...currentOptionItems
																.filter((currentOptionItem) => currentOptionItem.value === fullOfferingWithOptions.description)
																.map((currentOptionItem) => currentOptionItem.offeringid) as number[],
														];
													},
													virtualOptionItem.matchingOfferingIds,
												),
										),
									);
								}
							} else if (virtualOption) {
								const virtualOptionItem = virtualOption.items[virtualOption.items.length - 1];
								virtualOptionItem.matchingOfferingIds = Array.from(
									new Set(
										options
											.filter((currentOption) => 'variantid' in currentOption)
											.reduce(
												(matchingOfferingIds, currentOption) => {
													const currentOptionItems = currentOption.items as FullOfferingOptionValueVirtualIntersectModel[];

													return [
														...matchingOfferingIds,
														...currentOptionItems
															.filter((currentOptionItem) => currentOptionItem.value === fullOfferingWithOptions.description)
															.map((currentOptionItem) => currentOptionItem.offeringid) as number[],
													];
												},
												virtualOptionItem.matchingOfferingIds,
											),
									),
								);
							}

							/**
							 * If the option doesn't exist in the options list, add it to the
							 * options list
							 */
							if (!optionFound) {
								if (option.tag === 'size') {
									const manualSizeOption = options.find((optionIteration) => (
										optionIteration.tag === 'size'
										&& optionIteration.serialnumber === -1
									));

									if (manualSizeOption) {
										options.splice(
											options.indexOf(manualSizeOption),
											1,
										);
										option.tag = 'size';
									}
								}

								options.push(option);
							}
						}

						return options;
					},
					[] as (FullOfferingOptionVirtualIntersectModel | FullOfferingOptionIntersectModel)[],
				);

			if (!variantid) {
				return offeringOptionIntersectModels;
			}

			return offeringOptionIntersectModels.filter((option) => (
				(
					'variantid' in option
					&& option.variantid === variantid
				)
				|| !('variantid' in option)
			));
		};
	}

	public get getFullOfferingOptionValueIntersectModels(): (
		offeringOptions: PartialOfferingOptionIntersectModel[],
		offeringOption: PartialOfferingOptionIntersectModel,
		currentOfferingId?: number,
		offeringOptionValueFilter?: (
			offeringOptionValue: FullOfferingOptionValueIntersectModel,
			offeringOption: FullOfferingOptionIntersectModel,
		) => boolean,
	) => FullOfferingOptionValueIntersectModels {
		return (
			offeringOptions,
			offeringOption,
			currentOfferingId,
			offeringOptionValueFilter,
		) => {
			const productOption = offeringOptions
				.find((productOptionItem) => productOptionItem.id === offeringOption.id) as FullOfferingOptionIntersectModel;

			if (currentOfferingId) {
				/**
				 * Get the index of the product option in the list of product options
				 */
				const productOptionIndex = offeringOptions.indexOf(productOption);
				/**
				 * Get the list of product options that are previous to the current product option
				 * and that passes the filter (if any)
				 */
				const previousProductOptions = offeringOptions
					.filter((previousSelectOption, index) => {
						if (index < productOptionIndex) {
							let previousFilter = true;

							if (offeringOptionValueFilter) {
								previousFilter = previousSelectOption.items.some((productOptionValue) => (
									offeringOptionValueFilter(
										productOptionValue as FullOfferingOptionValueIntersectModel,
										previousSelectOption as FullOfferingOptionIntersectModel,
									)
								));
							}

							return previousFilter;
						}

						return false;
					});

				/**
				 * If there are no previous product options it's because it's the first product option,
				 * so return the full list of product option values
				 */
				if (previousProductOptions.length === 0) {
					return productOption.items;
				}

				/**
				 * Get the list of offering ids that are available for the previous product options
				 * but only the ones that include the current offering model selected
				 */
				const availableOfferingIdsVariations = Array.from(
					new Set(
						previousProductOptions
							.map((previousSelectOption) => (
								previousSelectOption.items
									.filter((productOptionValue) => {
										let previousFilter = true;

										if (offeringOptionValueFilter) {
											previousFilter = offeringOptionValueFilter(
												productOptionValue as FullOfferingOptionValueIntersectModel,
												previousSelectOption as FullOfferingOptionIntersectModel,
											);
										}

										return (
											previousFilter
											&& productOptionValue.matchingOfferingIds.includes(currentOfferingId)
										);
									})
									.map((item) => item.matchingOfferingIds)
							))
							.flat(),
					),
				);
				const [
					firstAvailableOfferingId,
					...remainingAvailableOfferingIds
				] = availableOfferingIdsVariations;
				const intersectedSetOfferingIds = new Set(firstAvailableOfferingId);

				/**
				 * Efficiently get the intersection of the offering ids between all the
				 * previous product options
				 */
				for (const remainingAvailableOfferingId of remainingAvailableOfferingIds) {
					for (const offeringId of intersectedSetOfferingIds) {
						if (!remainingAvailableOfferingId.includes(offeringId)) {
							intersectedSetOfferingIds.delete(offeringId);
						}
					}
				}

				const intersectedOfferingIds = Array.from(intersectedSetOfferingIds);
				/**
				 * Get the list of product option values for the current product option
				 * that match the offering ids from the intersection
				 */
				const productOptionItems = productOption.items
					.filter((productOptionValue) => (
						productOptionValue.matchingOfferingIds
							.find((matchingOfferingId) => (
								intersectedOfferingIds.includes(matchingOfferingId)
							))
					));

				/**
				 * Return the option values that match the intersection of the offering ids (can be empty if no matches were found)
				 */
				return productOptionItems;
			}

			return productOption.items;
		};
	}

	public get getMatchingFullOfferingModel(): (
		options: {
			offeringOption: PartialOfferingOptionIntersectModel,
			offeringOptionValue: PartialOfferingOptionValueIntersectModel,
			offeringModel: PartialOfferingModel,
			offeringModels: PartialOfferingModel[],
		},
	) => FullOfferingModel | undefined {
		return ({
			offeringOption,
			offeringOptionValue,
			offeringModel,
			offeringModels,
		}) => {
			/**
			 * Get all the product options models except the one that was changed
			 */
			const internalFullOfferingModelProductOptionsExceptCurrentOne = offeringModel.options
				.filter((option) => option.id !== offeringOption.id);
			/**
			 * Get all the product option values models except the one that was changed
			 */
			const optionValuesExceptCurrentOne = internalFullOfferingModelProductOptionsExceptCurrentOne
				.map((option) => option.value);
			/**
			 * Create a set of all the product option values ids that are
			 * currently selected, including the one that was changed
			 */
			const newOptionValuesForOfferingModel = [
				...optionValuesExceptCurrentOne,
				offeringOptionValue,
			];
			/**
			 * Create a potential set of product options combinations
			 * that could not match any offering model available for
			 * this PDP
			 */
			const failedOptions: PartialOfferingOptionModel[][] = [];
			let matchingOfferingModel = offeringModels
				.find((fullOfferingModel) => {
					const fullOfferingModelOptionsFailedToMatch: PartialOfferingOptionModel[] = [];
					const optionsMatching = fullOfferingModel.options
						.filter((option) => {
							/**
							 * Check if the current product option value matches the set of all
							 * product option values ids that are currently selected
							 */
							const optionFound = newOptionValuesForOfferingModel
								.find((newOption) => (
									(
										newOption.id
										&& newOption.id === option.value.id
									)
									|| (
										newOption.offeringid
										&& newOption.offeringid === option.value.offeringid
									)
								));

							/**
							 * If the current product option value does not match the set of all
							 * product option values ids that are currently selected, we add it
							 * to the list of product options that failed to match
							 */
							if (!optionFound) {
								fullOfferingModelOptionsFailedToMatch.push(option);
							}

							return optionFound;
						});

					const virtualOfferingOptionValueMatches = (
						offeringOptionValue.offeringid
						&& fullOfferingModel.id === offeringOptionValue.offeringid
					);

					if (
						optionsMatching.length !== Math.max(
							fullOfferingModel.options.length,
							newOptionValuesForOfferingModel.length,
						)
						&& !virtualOfferingOptionValueMatches
					) {
						/**
						 * Check if the current product option id is in the list of product options
						 * that failed to match
						 */
						const productOptionChangedFound = fullOfferingModelOptionsFailedToMatch
							.find((option) => option.id === offeringOption.id);

						/**
						 * If the current product option id is not in the list of product options
						 * that failed to match, we add it to the set of product options combinations
						 * that could not match any offering model available for this PDP
						 *
						 * We do this because for example if we had previously selected a set of
						 * product options orientation "square", size "20x20" and frame "white",
						 * and we changed orientation "square" for "portrait", we already know that
						 * the combination "portrait", "20x20" and "white" will not match any offering
						 * model available for this PDP, the orientation "sizes" are not
						 * shared between the two orientations, so we only care about the product
						 * options (except the current one) that failed to match, which in this case
						 * is "size", since "white" is matching in this example and in the set of
						 * all product option values that are currently selected
						 */
						if (!productOptionChangedFound) {
							failedOptions.push(fullOfferingModelOptionsFailedToMatch);
						}

						return false;
					}

					return true;
				});

			/**
			 * If we did not find any matching offering model, we try to find an
			 * intersection of the failed option ids between all the previous product options,
			 * if we find one, we know that the combination of product options that failed to
			 * match is not possible, so we remove it from the list of product options that
			 * needs to match in order to find a matching offering model
			 */
			if (!matchingOfferingModel) {
				const failedOptionIds = failedOptions
					.map((failedOptionList) => failedOptionList.map((failedOption) => failedOption.id));
				const [
					firstFailedOptionId,
					...remainingFailedOptionIds
				] = failedOptionIds;
				const intersectedFailedSetOptionIds = new Set(firstFailedOptionId);

				/**
				 * Efficiently get the intersection of the failed option ids between all the
				 * previous product options
				 */
				for (const remainingFailedOptionId of remainingFailedOptionIds) {
					for (const optionId of intersectedFailedSetOptionIds) {
						if (!remainingFailedOptionId.includes(optionId)) {
							intersectedFailedSetOptionIds.delete(optionId);
						}
					}
				}

				/**
				 * Convert the set of intersected failed option ids to an array
				 */
				const intersectedFailedOptionIds = Array.from(intersectedFailedSetOptionIds);

				/**
				 * If we found an intersection of the failed option ids between all the
				 * previous product options, we try to find a matching offering model
				 * that matches the set of all product option values ids that are currently
				 * selected, except for the intersected failed option ids by doing a filter
				 * on the options of each offering model
				 */
				matchingOfferingModel = offeringModels
					.find((fullOfferingModel) => (
						fullOfferingModel.options
							.filter((option) => !intersectedFailedOptionIds.find((failedOptionId) => failedOptionId === option.id))
							.every((option) => (
								newOptionValuesForOfferingModel.find((newOption) => newOption.id === option.value.id)
							))
					));
			}

			/**
			 * If we did not find any matching offering model, we try to find a matching
			 * offering model that matches only the product option value id that was changed
			 */
			if (!matchingOfferingModel) {
				matchingOfferingModel = offeringModels
					.find((fullOfferingModel) => (
						fullOfferingModel.options.find((option) => offeringOptionValue.id === option.value.id)
					));
			}

			return matchingOfferingModel as FullOfferingModel | undefined;
		};
	}

	public get getMoodSceneImage(): (
		projectModel: DB.ProductModel,
		projectVersion: number,
	) => HTMLImageElement | null {
		return (
			projectModel,
			projectVersion,
		) => {
			const productMoodScenes: MoodSceneModel | undefined = this._moodscenesimages[projectModel.id];

			if (
				productMoodScenes
				&& productMoodScenes.projectVersion === projectVersion
			) {
				return productMoodScenes.image;
			}

			return null;
		};
	}

	get getOfferingOptionValueModels() {
		return (
			offeringid: DB.OfferingModel['id'],
			regionid?: DB.RegionModel['id'],
		) => {
			const offeringOptionValueModels: DB.OfferingOptionValueModel[] = [];
			const offeringModel = this.getOffering(offeringid);

			if (offeringModel) {
				for (const offering of this.offerings) {
					let matches = true;

					if (offeringModel.flexgroupid) {
						matches = offering.flexgroupid === offeringModel.flexgroupid;
					} else {
						matches = (
							offering.groupid === offeringModel.groupid
							&& offering.typeid === offeringModel.typeid
						);
					}

					if (!matches) {
						continue;
					}

					if (regionid) {
						const regionOfferingLinkModel = this.findRegionOfferingLinkWhere({
							offeringid: offering.id,
							regionid,
						});

						if (!regionOfferingLinkModel) {
							matches = false;
						} else {
							const m = this.getOffering(regionOfferingLinkModel.offeringid);

							if (m
								&& !m.instock
							) {
								matches = false;
							}
						}
					}

					if (!matches) {
						continue;
					}

					for (const offeringoptionvalueofferinglink of this.offeringoptionvalueofferinglinks) {
						if (offeringoptionvalueofferinglink.offeringid === offering.id) {
							const offeringOptionValueModel = this.offeringoptionvalues
								.find((offeringoptionvalue) => offeringoptionvalue.id === offeringoptionvalueofferinglink.offeringoptionvalueid);

							if (
								offeringOptionValueModel
								&& !offeringOptionValueModels.includes(offeringOptionValueModel)
							) {
								offeringOptionValueModels.push(offeringOptionValueModel);
							}
						}
					}
				}
			}

			return offeringOptionValueModels;
		};
	}

	get getOfferingVariantName() {
		return (offeringId: number) => window.App.router.$t(
			`offerings:${offeringId}.variantname`,
			'',
		);
	}

	get getPDP() {
		return (id: DB.PDPModel['id']) => _.findWhere(
			this.pdps,
			{
				id,
			},
		);
	}

	get getPDPImages() {
		return (pdpid: DB.PDPModel['id']) => _.where(
			this.pdpimages,
			{
				pdpid,
			},
		);
	}

	get getListerItem() {
		return (id: number) => this.productcategories.find(
			(listerItem) => listerItem.id === id,
		);
	}

	public get isProductCategoriesDataFetchPending(): boolean {
		return this.productCategoriesDataFetchesPending > 0;
	}

	get getProductCategoryName() {
		return (id: number) => {
			const categoryModel = _.findWhere(
				this.productcategories,
				{
					id,
				},
			);
			const defaultName = categoryModel ? categoryModel.name : '';
			const name = window.App.router.$t(
				`productcategories:${id}.name`,
				defaultName,
			);
			return name;
		};
	}

	public get getUpsellDescription(): (id: number) => string {
		return (id) => {
			const upsellModel = this.findWhereUpsell({
				id,
			});
			const defaultUpsellDescription = (
				upsellModel?.description
					? upsellModel.description
					: ''
			);

			return (
				(
					window.App.router.$i18next.exists(`upsells.${id}`)
					&& window.App.router.$i18next.exists(`upsells.${id}.description`)
				)
					? window.App.router.$t(`upsells.${id}.description`)
					: defaultUpsellDescription
			);
		};
	}

	/**
	 * Resolve the display name for an upsell product, prioritizing the `upsells` namespace.
	 *
	 * @param upsellId Upsell record id.
	 * @param addDescription Indicates if the offering description should be appended.
	 * @returns The localized upsell display name.
	 */
	public get getUpsellName(): (
		upsellId: DB.UpsellModel['id'],
		addDescription?: boolean,
	) => string {
		return (
			upsellId,
			addDescription,
		) => {
			let name = window.App.router.$t(
				`upsells:${upsellId}.name`,
				'',
			);
			const offeringId = this.findWhereUpsell({
				id: upsellId,
			})?.upsellid;

			if (
				!name.length
				&& offeringId
			) {
				this.findAndGetOfferingWhere({
					id: offeringId,
				});

				return this.getOfferingName(
					offeringId,
					addDescription,
				);
			}
			if (!name.length) {
				return '';
			}

			if (addDescription) {
				const description = this.getUpsellDescription(upsellId);

				if (description.length) {
					name += ` | ${description}`;
				}
			}

			return name;
		};
	}

	get getProductGroup() {
		return (groupid: number) => _.findWhere(
			this.productgroups,
			{
				id: groupid,
			},
		);
	}

	get getRegion() {
		return (id: number) => _.findWhere(
			this.regions,
			{
				id,
			},
		);
	}

	@Action({
		rawError: true,
	})
	public async fetchMoodSceneImage(data: {
		projectModel: DB.ProductModel,
		projectVersion: number,
	}): Promise<void> {
		const moodScene = this.getMoodSceneImage(
			data.projectModel,
			data.projectVersion,
		);

		if (!moodScene) {
			const url = `${window.glAppUrl}api/product/${data.projectModel.id}/moodscenes`;
			const requestOptions: AxiosRequestConfig = {
				method: 'get',
				url,
			};

			return ajax
				.request(
					requestOptions,
					{
						auth: true,
					},
				)
				.then((response) => {
					if (response.data?.[0]?.url) {
						const responseData: Array<{ url: string; }> = response.data;
						const [{ url: moodSceneUrl }] = responseData;
						const image = new Image();
						image.onload = () => {
							this.setMoodSceneImage({
								projectId: data.projectModel.id,
								projectVersion: data.projectVersion,
								moodSceneImage: image,
							});
						};
						image.onerror = (error) => {
							throw error;
						};
						image.src = moodSceneUrl;
					} else {
						let serverResponse = response.data;

						if (typeof serverResponse === 'object') {
							serverResponse = JSON.stringify(serverResponse);
						}

						throw new Error(`Could not generate the moodscene image, server response: ${serverResponse}`);
					}
				})
				.catch((error) => {
					reportError(
						error,
						'error',
					);

					UIModule.openDialog({
						header: componentSlot({
							slot: window.App.router.$t('dialogHeaderError'),
						}),
						body: componentSlot({
							slot: window.App.router.$t('dialogTextMoodSceneLoadError'),
						}),
						footer: componentSlot({
							tag: ButtonComponent,
							props: {
								class: 'grow',
								onClick: UIModule.closeDialog,
							},
							slot: window.App.router.$t('dialogButtonErrorOk'),
						}),
						props: {
							onClose: UIModule.closeDialog,
							'onClick:outside': UIModule.closeDialog,
						},
					});
				});
		}

		return undefined;
	}

	get findBulkProductTypeWhere() {
		return (properties: Partial<DB.BulkProductTypeModel>) => _.findWhere(
			this.bulkproducttypes,
			properties,
		);
	}

	get findBulkQuantity() {
		return (properties: Partial<DB.BulkQuantityModel>) => _.where(
			this.bulkquantities,
			properties,
		);
	}

	get findBulkQuantityWhere() {
		return (properties: Partial<DB.BulkQuantityModel>) => _.findWhere(
			this.bulkquantities,
			properties,
		);
	}

	get findCountry() {
		return (properties: Partial<DB.CountryModel>) => _.where(
			this.countries,
			properties,
		);
	}

	get findCountryWhere() {
		return (properties: Partial<DB.CountryModel>) => _.findWhere(
			this.countries,
			properties,
		);
	}

	get defaultCurrency() {
		return this.currencies.find((currencyModel) => currencyModel.default);
	}

	get findHandlingWhere() {
		return (properties: Partial<DB.HandlingModel>) => _.findWhere(
			this.handling,
			properties,
		);
	}

	get findHyperlink() {
		return (
			searchProps: Partial<DB.HyperlinkModel>,
			strict = false,
		) => {
			const userLanguage = this.context.rootState.user.language;

			if (userLanguage) {
				searchProps.languageid = userLanguage;
			}

			const userCountryId = this.context.rootState.user.countryid;
			const countryModel = userCountryId
				? this.countries.find((country) => country.id === userCountryId)
				: undefined;

			if (countryModel) {
				searchProps.regionid = countryModel.regionid;
			}

			let hyperlinkModel = this.hyperlinks
				.find((hyperlink) => {
					for (const property in searchProps) {
						if (
							Object.prototype.hasOwnProperty.call(
								searchProps,
								property,
							)
						) {
							const castedProperty = property as keyof DB.HyperlinkModel;

							if (hyperlink[castedProperty] !== searchProps[castedProperty]) {
								return false;
							}
						}
					}

					return true;
				});

			if (
				!strict
				&& !hyperlinkModel
				&& searchProps.languageid
			) {
				// Search again without the language constraint
				delete searchProps.languageid;
				hyperlinkModel = this.hyperlinks
					.find((hyperlink) => {
						for (const property in searchProps) {
							if (
								Object.prototype.hasOwnProperty.call(
									searchProps,
									property,
								)
							) {
								const castedProperty = property as keyof DB.HyperlinkModel;

								if (hyperlink[castedProperty] !== searchProps[castedProperty]) {
									return false;
								}
							}
						}

						return true;
					});
			}

			if (
				!strict
				&& !hyperlinkModel
				&& searchProps.regionid
			) {
				// Search again without the region constraint
				delete searchProps.regionid;
				hyperlinkModel = this.hyperlinks
					.find((hyperlink) => {
						for (const property in searchProps) {
							if (
								Object.prototype.hasOwnProperty.call(
									searchProps,
									property,
								)
							) {
								const castedProperty = property as keyof DB.HyperlinkModel;

								if (hyperlink[castedProperty] !== searchProps[castedProperty]) {
									return false;
								}
							}
						}

						return true;
					});
			}

			return hyperlinkModel;
		};
	}

	get findLanguageWhere() {
		return (properties: Partial<DB.LanguageModel>) => _.findWhere(
			this.languages,
			properties,
		);
	}

	get findLocalizedBadgeImages() {
		return (
			badgeIds: DB.BadgeImageModel['id'][],
		): DB.BadgeImageModel[] => {
			const badgeImageModels: DB.BadgeImageModel[] = [];
			badgeIds.forEach((badgeId) => {
				const searchProps: Partial<DB.BadgeImageModel> = {
					badgeid: badgeId,
					languageid: null,
					regionid: null,
				};

				const userLanguage = this.context.rootState.user.language;

				if (userLanguage) {
					searchProps.languageid = userLanguage;
				}

				const userCountryId = this.context.rootState.user.countryid;

				const countryModel = userCountryId
					? this.countries.find((country) => country.id === userCountryId)
					: undefined;

				if (countryModel) {
					searchProps.regionid = countryModel.regionid;
				}

				let badgeImageModel = this.badgeimages
					.find((badgeimage) => {
						for (const property in searchProps) {
							if (
								Object.prototype.hasOwnProperty.call(
									searchProps,
									property,
								)
							) {
								const castedProperty = property as keyof DB.BadgeImageModel;

								if (badgeimage[castedProperty] !== searchProps[castedProperty]) {
									return false;
								}
							}
						}

						return true;
					});

				if (
					!badgeImageModel
					&& searchProps.languageid
				) {
					// Search again without the language constraint
					badgeImageModel = this.badgeimages
						.find((badgeimage) => {
							const searchPropsWithoutLanguageId = {
								...searchProps,
								languageid: null,
							};

							for (const property in searchPropsWithoutLanguageId) {
								if (
									Object.prototype.hasOwnProperty.call(
										searchPropsWithoutLanguageId,
										property,
									)
								) {
									const castedProperty = property as keyof DB.BadgeImageModel;

									if (badgeimage[castedProperty] !== searchPropsWithoutLanguageId[castedProperty]) {
										return false;
									}
								}
							}

							return true;
						});
				}

				if (
					!badgeImageModel
					&& searchProps.regionid
				) {
					// Search again without the region constraint
					badgeImageModel = this.badgeimages
						.find((badgeimage) => {
							const searchPropsWithoutRegionId = {
								...searchProps,
								regionid: null,
							};

							for (const property in searchPropsWithoutRegionId) {
								if (
									Object.prototype.hasOwnProperty.call(
										searchPropsWithoutRegionId,
										property,
									)
								) {
									const castedProperty = property as keyof DB.BadgeImageModel;

									if (badgeimage[castedProperty] !== searchPropsWithoutRegionId[castedProperty]) {
										return false;
									}
								}
							}

							return true;
						});
				}

				if (!badgeImageModel) {
					// Search again without the region and language constraints
					badgeImageModel = this.badgeimages
						.find((badgeimage) => {
							const searchPropsWithoutLanguageIdAndRegionId = {
								...searchProps,
								regionid: null,
								languageid: null,
							};

							for (const property in searchPropsWithoutLanguageIdAndRegionId) {
								if (
									Object.prototype.hasOwnProperty.call(
										searchPropsWithoutLanguageIdAndRegionId,
										property,
									)
								) {
									const castedProperty = property as keyof DB.BadgeImageModel;

									if (badgeimage[castedProperty] !== searchPropsWithoutLanguageIdAndRegionId[castedProperty]) {
										return false;
									}
								}
							}

							return true;
						});
				}

				if (badgeImageModel) {
					badgeImageModels.push(badgeImageModel);
				}
			});

			return badgeImageModels;
		};
	}

	public get findOfferingFrameImage() {
		return (offeringframeimage: DB.OfferingFrameImageModel): HTMLImageElement | undefined => (
			this._offeringframesimages[offeringframeimage.id]
		);
	}

	public get findOfferingMaskImage() {
		return (
			offering: DB.OfferingModel,
			pageIndex = 0,
		): HTMLImageElement | undefined => {
			const source = resolveOfferingMaskAssetSource(
				this.offeringmaskimages,
				offering,
				pageIndex,
			);

			return source
				? this._offeringmasksimages[source.cacheKey]
				: undefined;
		};
	}

	public get findOfferingOverlayImage() {
		return (
			offering: DB.OfferingModel,
			pageIndex = 0,
		): HTMLImageElement | undefined => {
			const source = resolveOfferingOverlayAssetSource(
				this.offeringoverlayimages,
				offering,
				pageIndex,
			);

			return source
				? this._offeringoverlaysimages[source.cacheKey]
				: undefined;
		};
	}

	public get findOffering(): (properties: Partial<DB.OfferingModel>) => DB.OfferingModel[] {
		return (properties) => (
			this.offerings.filter((offering) => {
				for (const property in properties) {
					if (
						Object.prototype.hasOwnProperty.call(
							properties,
							property,
						)
					) {
						const castedProperty = property as keyof DB.OfferingModel;

						if (offering[castedProperty] !== properties[castedProperty]) {
							return false;
						}
					}
				}

				return true;
			})
		);
	}

	public get findOfferingPageSpecification() {
		return (
			offeringId: DB.OfferingModel['id'],
			pageIndex: number,
		): API.OfferingDataPageSpecification | undefined => findOfferingPageSpecification(
			this.offeringpages,
			offeringId,
			pageIndex,
		);
	}

	public get findOfferingEditorMessages() {
		return (
			offeringId: DB.OfferingModel['id'],
		): API.OfferingDataEditorMessageModel[] => findAllOfferingEditorMessages(
			this.editorMessages,
			offeringId,
		).map(localizeOfferingEditorMessage);
	}

	get findOfferingBadges() {
		return (
			offeringid: DB.OfferingModel['id'],
		) => {
			const linkModels = _.where(
				this.badgeofferinglinks,
				{
					offeringid,
				},
			);
			const badgeIds = linkModels.map(
				(linkModel) => linkModel.badgeid,
			);
			return this.findLocalizedBadgeImages(
				badgeIds,
			);
		};
	}

	@Action({
		rawError: true,
	})
	public async findOfferings(offeringIds: number[]): Promise<void> {
		const offeringIdsNotFound = offeringIds
			.map((offeringId) => ({
				offeringId,
				offeringModel: this.offerings.find((offering) => offering.id === offeringId),
			}))
			.filter(({ offeringModel }) => !offeringModel)
			.map(({ offeringId }) => offeringId);

		if (offeringIdsNotFound.length) {
			await this.fetchOfferingsData({
				offeringIds: offeringIdsNotFound,
			});
		}
	}

	public get findAndGetOfferingWhere(): (properties: Partial<DB.OfferingModel>) => Promise<DB.OfferingModel | undefined> {
		return async (properties) => {
			const offeringModelFound = this.findOfferingWhere(properties);

			if (!offeringModelFound) {
				await findAndSetOffering(properties);
			}

			return this.findOfferingWhere(properties);
		};
	}

	get findOfferingWhere() {
		return (properties: Partial<DB.OfferingModel>): DB.OfferingModel | undefined => (
			this.offerings.find((offering) => {
				for (const property in properties) {
					if (
						Object.prototype.hasOwnProperty.call(
							properties,
							property,
						)
					) {
						const castedProperty = property as keyof DB.OfferingModel;

						if (
							castedProperty === 'externalid'
							&& `${offering.externalid}` !== properties.externalid?.toString()
						) {
							return false;
						}

						if (offering[castedProperty] !== properties[castedProperty]) {
							return false;
						}
					}
				}

				return true;
			})
		);
	}

	get findPDPBadges() {
		return (pdpid: DB.PDPModel['id']) => {
			const linkModels = (this.badgepdplinks || []).filter((badgepdplink) => badgepdplink.pdpid === pdpid);
			const badgeIds = linkModels.map((linkModel) => linkModel.badgeid);

			return this.findLocalizedBadgeImages(
				badgeIds,
			);
		};
	}

	public get findPricingWhere(): (properties: Partial<DB.PricingModel>) => DB.PricingModel | undefined {
		return (properties) => {
			const priceModel = this.pricing.find((pricing) => {
				for (const property in properties) {
					if (
						Object.prototype.hasOwnProperty.call(
							properties,
							property,
						)
					) {
						const castedProperty = property as keyof DB.PricingModel;

						if (pricing[castedProperty] !== properties[castedProperty]) {
							return false;
						}
					}
				}

				return true;
			});

			if (!priceModel) {
				return undefined;
			}

			const multiplier = AppStateModule.pricingMultiplier;

			return {
				...priceModel,
				price_base_from: priceModel.price_base_from * multiplier,
				price_base: priceModel.price_base * multiplier,
				price_page_from: priceModel.price_page_from * multiplier,
				price_page: priceModel.price_page * multiplier,
			};
		};
	}

	get findListerItems() {
		return (properties: Partial<DB.ProductCategoryModel>) => _.where(
			this.productcategories,
			properties,
		);
	}

	get findProductCategoryBadges() {
		return (
			listerItemId: DB.ProductCategoryModel['id'],
		) => {
			const linkModels = _.where(
				this.badgeproductcategorylinks,
				{
					productcategoryid: listerItemId,
				},
			);
			const badgeIds = linkModels.map(
				(linkModel) => linkModel.badgeid,
			);
			return this.findLocalizedBadgeImages(
				badgeIds,
			);
		};
	}

	get findRegion() {
		return (properties: Partial<DB.RegionModel>) => _.where(
			this.regions,
			properties,
		);
	}

	get findRegionCurrencyLink() {
		return (properties: Partial<DB.RegionCurrencyModel>) => _.where(
			this.regioncurrencylinks,
			properties,
		);
	}

	get findRegionCurrencyLinkWhere() {
		return (properties: Partial<DB.RegionCurrencyModel>) => _.findWhere(
			this.regioncurrencylinks,
			properties,
		);
	}

	get findRegionOfferingLink() {
		return (properties: Partial<DB.RegionOfferingModel>) => _.where(
			this.regionofferinglinks,
			properties,
		);
	}

	get findRegionOfferingLinkWhere() {
		return (properties: Partial<DB.RegionOfferingModel>) => this.regionofferinglinks.find((regionofferinglink) => {
			for (const property in properties) {
				if (
					Object.prototype.hasOwnProperty.call(
						properties,
						property,
					)
				) {
					const castedProperty = property as keyof DB.RegionOfferingModel;

					if (regionofferinglink[castedProperty] !== properties[castedProperty]) {
						return false;
					}
				}
			}

			return true;
		});
	}

	get findStateProv() {
		return (properties: Partial<DB.StateProvModel>) => _.where(
			this.stateprovs,
			properties,
		);
	}

	get findStateProvWhere() {
		return (properties: Partial<DB.StateProvModel>) => _.findWhere(
			this.stateprovs,
			properties,
		);
	}

	public get findWhereUpsell(): (properties: Partial<DB.UpsellModel>) => DB.UpsellModel | undefined {
		return (properties) => (
			this.upsells.find((upsell) => {
				for (const property in properties) {
					if (
						Object.prototype.hasOwnProperty.call(
							properties,
							property,
						)
					) {
						const castedProperty = property as keyof DB.UpsellModel;

						if (upsell[castedProperty] !== properties[castedProperty]) {
							return false;
						}
					} else {
						return false;
					}
				}

				return true;
			})
		);
	}

	public get getOfferingFrame() {
		return (
			offeringId: DB.OfferingModel['id'],
			pageIndex = 0,
		): OfferingFrameModel | undefined => {
			const linkModels = this.offeringofferingframelinks.filter((model) => (
				model.offeringid === offeringId
			));
			const linkModel = (
				linkModels.find((model) => model.pageindex === pageIndex)
				?? linkModels[0]
			);

			if (!linkModel) {
				return undefined;
			}

			const offeringFrameModel = this.offeringframes.find((model) => (
				model.id === linkModel.offeringframeid
			));

			if (!offeringFrameModel) {
				return undefined;
			}

			const templateModel = this.offeringframetemplates.find((offeringFrameTemplateModel) => (
				offeringFrameTemplateModel.id === offeringFrameModel.offeringframetemplateid
			));
			const imageModel = this.offeringframeimages.find((offeringFrameImageModel) => (
				offeringFrameImageModel.id === offeringFrameModel.offeringframeimageid
			));

			if (
				!imageModel
				|| !templateModel
			) {
				return undefined;
			}

			if (
				!imageModel.height
				|| !imageModel.width
			) {
				loadOfferingFrameImage(imageModel);
			}

			return {
				id: offeringFrameModel.id,
				overpage: Boolean(offeringFrameModel.overpage),
				required: Boolean(offeringFrameModel.required),
				imageModel,
				templateModel,
			};
		};
	}

	public get getOfferingMask() {
		return (
			offeringId: DB.OfferingModel['id'],
			pageIndex = 0,
		): DB.OfferingMaskImageModel | undefined => (
			findOfferingPageAssetModel(
				this.offeringmaskimages,
				offeringId,
				pageIndex,
			)
		);
	}

	public get getOfferingOverlay() {
		return (
			offeringId: DB.OfferingModel['id'],
			pageIndex = 0,
		): DB.OfferingOverlayImageModel | undefined => (
			findOfferingPageAssetModel(
				this.offeringoverlayimages,
				offeringId,
				pageIndex,
			)
		);
	}

	public get hasOfferingMask() {
		return (
			offering: DB.OfferingModel,
			pageIndex = 0,
		): boolean => (
			resolveOfferingMaskAssetSource(
				this.offeringmaskimages,
				offering,
				pageIndex,
			) !== null
		);
	}

	public get hasOfferingOverlay() {
		return (
			offering: DB.OfferingModel,
			pageIndex = 0,
		): boolean => (
			resolveOfferingOverlayAssetSource(
				this.offeringoverlayimages,
				offering,
				pageIndex,
			) !== null
		);
	}

	get productGroupName() {
		return (
			groupid: DB.ProductGroupModel['id'],
		): string => {
			const productGroupModel = _.findWhere(
				this.productgroups,
				{
					id: groupid,
				},
			);

			if (productGroupModel) {
				return window.App.router.$i18next.exists(`productLabels.${groupid}`)
					? window.App.router.$t(`productLabels.${groupid}`)
					: productGroupModel.name;
			}

			return '';
		};
	}

	public get whereUpsell(): (properties: Partial<DB.UpsellModel>) => DB.UpsellModel[] {
		return (properties: Partial<DB.UpsellModel>) => (
			this.upsells.filter((upsell) => {
				for (const property in properties) {
					if (
						Object.prototype.hasOwnProperty.call(
							properties,
							property,
						)
					) {
						const castedProperty = property as keyof DB.UpsellModel;

						if (upsell[castedProperty] !== properties[castedProperty]) {
							return false;
						}
					} else {
						return false;
					}
				}

				return true;
			})
		);
	}

	/*
	 * @Mutation
	 * public changeOffering(
	 * data: OptionalExceptFor<OfferingModel, 'id'>,
	 * ) {
	 * const i = _.findIndex(this.offerings, { id: data.id });
	 * if (i >= 0) {
	 * const model = this.offerings[i];
	 * Vue.set(
	 * this.offerings,
	 * i,
	 * _.extend(model, data),
	 * );
	 * }
	 * }
	 */

	@Mutation
	setBadges(models?: DB.BadgeModel[]) {
		this.badges.splice(
			0,
			this.badges.length,
			...(
				models
				?? []
			),
		);
	}

	@Mutation
	setBadgeImages(models?: DB.BadgeImageModel[]) {
		this.badgeimages.splice(
			0,
			this.badgeimages.length,
			...(
				models
				?? []
			),
		);
	}

	@Mutation
	setBadgeOfferingLinks(models?: DB.BadgeOfferingModel[]) {
		this.badgeofferinglinks.splice(
			0,
			this.badgeofferinglinks.length,
			...(
				models
				?? []
			),
		);
	}

	@Mutation
	setBadgePDPLinks(models: DB.BadgePDPModel[]) {
		this.badgepdplinks.splice(
			0,
			this.badgepdplinks.length,
			...(
				models
				?? []
			),
		);
	}

	@Mutation
	setBadgeProductCategoryLinks(models: DB.BadgeProductCategoryModel[]) {
		this.badgeproductcategorylinks.splice(
			0,
			this.badgeproductcategorylinks.length,
			...(
				models
				?? []
			),
		);
	}

	@Mutation
	setBuckets(buckets: DB.BucketModel[]) {
		this.buckets.splice(
			0,
			this.buckets.length,
			...buckets,
		);
	}

	@Mutation
	setBulks(models: DB.BulkModel[]) {
		this.bulks.splice(
			0,
			this.bulks.length,
			...models,
		);
	}

	@Mutation
	setBulkProductTypes(models: DB.BulkProductTypeModel[]) {
		this.bulkproducttypes.splice(
			0,
			this.bulkproducttypes.length,
			...models,
		);
	}

	@Mutation
	setBulkQuantities(models: DB.BulkQuantityModel[]) {
		this.bulkquantities.splice(
			0,
			this.bulkquantities.length,
			..._.sortBy(
				models,
				'from',
			),
		);
	}

	@Mutation
	setCountries(countries: DB.CountryModel[]) {
		this.countries.splice(
			0,
			this.countries.length,
			...countries,
		);
	}

	@Mutation
	setCurrencies(currencies: DB.CurrencyModel[]) {
		this.currencies.splice(
			0,
			this.currencies.length,
			...currencies,
		);
	}

	@Mutation
	setFlexgroups(flexgroups: API.FlexGroupModel[]) {
		this.flexgroups.splice(
			0,
			this.flexgroups.length,
			...flexgroups,
		);
	}

	@Mutation
	setHandling(handling: DB.HandlingModel[]) {
		this.handling.splice(
			0,
			this.handling.length,
			...handling,
		);
	}

	@Mutation
	setHyperlinks(hyperlinks: DB.HyperlinkModel[]) {
		this.hyperlinks.splice(
			0,
			this.hyperlinks.length,
			...hyperlinks,
		);
	}

	@Mutation
	setLanguages(languages: DB.LanguageModel[]) {
		this.languages.splice(
			0,
			this.languages.length,
			...languages,
		);
	}

	@Mutation
	setModels2D(models: DB.Model2DModel[]) {
		this.models2d.splice(
			0,
			this.models2d.length,
			...models,
		);
	}

	@Mutation
	setModels3D(models: DB.Model3DModel[]) {
		this.models3d.splice(
			0,
			this.models3d.length,
			...models,
		);
	}

	@Mutation
	public setMoodSceneImage(data: {
		projectId: DB.ProductModel['id'];
		projectVersion: number;
		moodSceneImage: HTMLImageElement;
	}) {
		this._moodscenesimages[data.projectId] = {
			projectVersion: data.projectVersion,
			image: data.moodSceneImage,
		};
	}

	@Mutation
	setOfferingFrames(offeringframes: DB.OfferingFrameModel[]) {
		this.offeringframes.splice(
			0,
			this.offeringframes.length,
			...offeringframes,
		);
	}

	@Mutation
	setOfferingFrameImages(offeringframeimages: DB.OfferingFrameImageModel[]) {
		this.offeringframeimages.splice(
			0,
			this.offeringframeimages.length,
			...offeringframeimages,
		);
	}

	@Mutation
	setOfferingMaskImages(offeringmaskimages: DB.OfferingMaskImageModel[]) {
		this.offeringmaskimages.splice(
			0,
			this.offeringmaskimages.length,
			...offeringmaskimages,
		);
	}

	@Mutation
	setOfferingOverlayImages(offeringoverlayimages: DB.OfferingOverlayImageModel[]) {
		this.offeringoverlayimages.splice(
			0,
			this.offeringoverlayimages.length,
			...offeringoverlayimages,
		);
	}

	@Mutation
	setOfferingFrameTemplates(offeringframetemplates: DB.OfferingFrameTemplateModel[]) {
		this.offeringframetemplates.splice(
			0,
			this.offeringframetemplates.length,
			...offeringframetemplates,
		);
	}

	@Mutation
	protected _setOfferingFrameImage(
		payload: {
			offeringframeimageid: DB.OfferingFrameImageModel['id'];
			image: HTMLImageElement;
		},
	) {
		this._offeringframesimages[payload.offeringframeimageid] = payload.image;
	}

	@Mutation
	protected _setOfferingMaskImage(
		payload: {
			cacheKey: string;
			image: HTMLImageElement;
		},
	) {
		this._offeringmasksimages[payload.cacheKey] = payload.image;
	}

	@Mutation
	setOfferingOfferingFrameLinks(models: DB.OfferingOfferingFrameLinkModel[]) {
		this.offeringofferingframelinks.splice(
			0,
			this.offeringofferingframelinks.length,
			...models,
		);
	}

	@Mutation
	protected _setOfferingOverlayImage(
		payload: {
			cacheKey: string;
			image: HTMLImageElement;
		},
	) {
		this._offeringoverlaysimages[payload.cacheKey] = payload.image;
	}

	@Mutation
	setOfferings(offerings: DB.OfferingModel[]) {
		const parsedOfferings = offerings.map(normalizeOfferingModel);
		this.offerings.splice(
			0,
			this.offerings.length,
			..._.sortBy(
				parsedOfferings,
				'cost_base',
			),
		);
	}

	@Mutation
	setOfferingOptions(offeringoptions: DB.OfferingOptionModel[]) {
		this.offeringoptions.splice(
			0,
			this.offeringoptions.length,
			..._.sortBy(
				offeringoptions,
				'serialnumber',
			),
		);
	}

	@Mutation
	setOfferingOptionValues(offeringoptionvalues: DB.OfferingOptionValueModel[]) {
		this.offeringoptionvalues.splice(
			0,
			this.offeringoptionvalues.length,
			..._.sortBy(
				offeringoptionvalues,
				'serialnumber',
			),
		);
	}

	@Mutation
	setOfferingOptionValueOfferingLinks(offeringoptionvalueofferinglinks: DB.OfferingOptionValueOfferingModel[]) {
		this.offeringoptionvalueofferinglinks.splice(
			0,
			this.offeringoptionvalueofferinglinks.length,
			...offeringoptionvalueofferinglinks,
		);
	}

	@Mutation
	setEditorMessages(editorMessages: API.OfferingDataEditorMessageModel[]) {
		this.editorMessages.splice(
			0,
			this.editorMessages.length,
			...editorMessages,
		);
	}

	@Mutation
	setOfferingPages(offeringpages: API.OfferingDataPageSpecification[]) {
		this.offeringpages.splice(
			0,
			this.offeringpages.length,
			...offeringpages,
		);
	}

	@Mutation
	setPDPs(pdps: API.PDPModel[]) {
		this.pdps.splice(
			0,
			this.pdps.length,
			...pdps,
		);
	}

	@Mutation
	setPDPFilters(pdpfilters: DB.PDPFilterModel[]) {
		this.pdpfilters.splice(
			0,
			this.pdpfilters.length,
			...pdpfilters,
		);
	}

	@Mutation
	setPDPFilterValues(pdpfiltervalues: DB.PDPFilterValueModel[]) {
		this.pdpfiltervalues.splice(
			0,
			this.pdpfiltervalues.length,
			...pdpfiltervalues,
		);
	}

	@Mutation
	setPDPFilterValueOfferingLinks(pdpfiltervalueofferinglinks: DB.PDPFilterValueOfferingModel[]) {
		this.pdpfiltervalueofferinglinks.splice(
			0,
			this.pdpfiltervalueofferinglinks.length,
			...pdpfiltervalueofferinglinks,
		);
	}

	@Mutation
	setPDPImages(pdpimages: DB.PDPImageModel[]) {
		this.pdpimages.splice(
			0,
			this.pdpimages.length,
			...pdpimages,
		);
	}

	@Mutation
	setPricing(pricing: DB.PricingModel[]) {
		this.pricing.splice(
			0,
			this.pricing.length,
			...pricing,
		);
	}

	@Mutation
	setProductCategories(categories: API.ProductCategoryModel[]) {
		this.productcategories.splice(
			0,
			this.productcategories.length,
			..._.sortBy(
				categories,
				'serialnumber',
			),
		);
	}

	@Mutation
	setProductGroups(productgroups: DB.ProductGroupModel[]) {
		this.productgroups.splice(
			0,
			this.productgroups.length,
			...productgroups,
		);
	}

	@Mutation
	setRegionCurrencyLinks(models: DB.RegionCurrencyModel[]) {
		this.regioncurrencylinks.splice(
			0,
			this.regioncurrencylinks.length,
			...models,
		);
	}

	@Mutation
	setRegionOfferingLinks(models: DB.RegionOfferingModel[]) {
		this.regionofferinglinks.splice(
			0,
			this.regionofferinglinks.length,
			...models,
		);
	}

	@Mutation
	setRegions(regions: DB.RegionModel[]) {
		this.regions.splice(
			0,
			this.regions.length,
			...regions,
		);
	}

	@Mutation
	setStateProvs(stateprovs: DB.StateProvModel[]) {
		this.stateprovs.splice(
			0,
			this.stateprovs.length,
			..._.sortBy(
				stateprovs,
				'name',
			),
		);
	}

	@Mutation
	private setTranslations(translations?: DB.TranslationModel[]) {
		if (translations) {
			for (const translationRecord of translations) {
				let newBundle = translationRecord.bundle;
				const oldBundle = window.App.router.$i18next.getResourceBundle(
					translationRecord.language,
					translationRecord.namespace,
				);

				if (oldBundle) {
					newBundle = mergeTranslationBundles(
						oldBundle,
						newBundle,
					);
				}

				window.App.router.$i18next.addResourceBundle(
					translationRecord.language,
					translationRecord.namespace,
					newBundle,
					true,
					true,
				);
			}
		}
	}

	@Mutation
	setUpsells(models: DB.UpsellModel[]) {
		this.upsells.splice(
			0,
			this.upsells.length,
			...models,
		);
	}

	@Mutation
	protected _unsetOfferingFrameImage(
		offeringframeimageid: DB.OfferingFrameImageModel['id'],
	) {
		delete this._offeringframesimages[offeringframeimageid];
	}

	@Mutation
	protected _unsetOfferingMaskImage(
		cacheKey: string,
	) {
		delete this._offeringmasksimages[cacheKey];
	}

	@Mutation
	protected _unsetOfferingOverlayImage(
		cacheKey: string,
	) {
		delete this._offeringoverlaysimages[cacheKey];
	}

	@Action({
		rawError: true,
	})
	public async resetOfferingData(): Promise<void> {
		this.setEditorMessages([]);
		this.setOfferings([]);
		this.setOfferingOptions([]);
		this.setOfferingOptionValues([]);
		this.setOfferingOptionValueOfferingLinks([]);
		this.setOfferingPages([]);
		this.setRegionOfferingLinks([]);
		this.setOfferingFrames([]);
		this.setOfferingFrameImages([]);
		this.setOfferingMaskImages([]);
		this.setOfferingOverlayImages([]);
		this.setOfferingFrameTemplates([]);
		this.setOfferingOfferingFrameLinks([]);
	}

	@Action({
		rawError: true,
	})
	public fetchProductCategoriesData({
		requestOptions,
		methodOptions,
	}: {
		requestOptions?: AxiosRequestConfig;
		methodOptions?: AjaxOptions;
	} = {}): Promise<void> {
		const fetchKey = getCurrentProductCategoriesDataFetchKey();
		const pendingFetch = productCategoriesDataFetchPromises.get(fetchKey);

		if (pendingFetch) {
			return pendingFetch;
		}

		if (completedProductCategoriesDataFetchKey === fetchKey) {
			return Promise.resolve();
		}

		const defaultRequestOptions: AxiosRequestConfig = {
			method: 'get',
			url: `${window.glAppUrl}api/app/productcategoriesdata`,
		};
		const defaultMethodOptions: AjaxOptions = {
			auth: false,
			debug: {
				dialog: true,
			},
		};

		requestOptions = (
			requestOptions
				? merge(
					defaultRequestOptions,
					requestOptions,
				)
				: defaultRequestOptions
		);
		methodOptions = (
			methodOptions
				? merge(
					defaultMethodOptions,
					methodOptions,
				)
				: defaultMethodOptions
		);

		const fetchPromise = (async () => {
			if (requestOptions.url) {
				const url = new URL(requestOptions.url);

				if (window.glStack !== 'live') {
					// We always want to get the latest data from the server when in dev/test/staging mode
					url.searchParams.set(
						'version',
						(new Date().getTime() / 1000).toString(),
					);
				} else if (window.appDataVersion) {
					// In production we want to make use of cloudFront caching, so we need a version number
					url.searchParams.set(
						'version',
						window.appDataVersion,
					);
				}

				if (UserModule.countryid) {
					url.searchParams.set(
						'countryid',
						UserModule.countryid.toString(),
					);
				}

				if (UserModule.currency) {
					url.searchParams.set(
						'usercurrency',
						UserModule.currency,
					);
				}

				if (UserModule.language) {
					url.searchParams.set(
						'userlanguage',
						UserModule.language,
					);
				}

				const shopId = await UserModule.getShopId();

				if (shopId) {
					url.searchParams.set(
						'shopid',
						shopId.toString(),
					);
				}

				requestOptions.url = url.toString();
			}

			return ajax
				.request(
					requestOptions,
					methodOptions,
				)
				.then((response) => {
					/*
					 * A fetch for different attributes may have started after this
					 * one. Only the request matching the current attributes may
					 * mutate store state, so a slow stale response cannot overwrite
					 * fresh data (or mark a non-current key as completed).
					 */
					if (getCurrentProductCategoriesDataFetchKey() !== fetchKey) {
						return undefined;
					}

					const responseData = response.data;
					return this.setProductCategories(_.toArray(responseData.productcategories));
				})
				.then(() => {
					if (getCurrentProductCategoriesDataFetchKey() === fetchKey) {
						completedProductCategoriesDataFetchKey = fetchKey;
					}
					return undefined;
				});
		})();

		const trackedFetchPromise = fetchPromise.finally(() => {
			if (productCategoriesDataFetchPromises.get(fetchKey) === trackedFetchPromise) {
				productCategoriesDataFetchPromises.delete(fetchKey);
				this._setProductCategoriesDataFetchesPending(productCategoriesDataFetchPromises.size);
			}
		});

		productCategoriesDataFetchPromises.set(
			fetchKey,
			trackedFetchPromise,
		);
		this._setProductCategoriesDataFetchesPending(productCategoriesDataFetchPromises.size);

		return trackedFetchPromise;
	}

	@Mutation
	private _setProductCategoriesDataFetchesPending(pending: number) {
		this.productCategoriesDataFetchesPending = pending;
	}

	@Mutation
	private _queueOfferingsDataMerge(mergePromise: Promise<void>) {
		this._offeringsDataMergeQueue = mergePromise;
	}

	/**
	 * Queues offerings data merges so concurrent responses apply sequentially.
	 *
	 * @param responseData {@link API.OfferingsDataResponse} payload to merge.
	 * @returns Promise resolved when the merge completes.
	 */
	@Action
	private queueOfferingsDataMerge(responseData: API.OfferingsDataResponse): Promise<void> {
		const mergePromise = this._offeringsDataMergeQueue
			.catch(() => undefined)
			.then(() => this.applyOfferingsData(responseData));
		this._queueOfferingsDataMerge(mergePromise);

		return mergePromise;
	}

	/**
	 * Applies offerings data to the store by merging new records into existing arrays.
	 *
	 * @param responseData {@link API.OfferingsDataResponse} payload to merge.
	 * @returns Promise resolved after updates are applied.
	 */
	@Action
	private applyOfferingsData(responseData: API.OfferingsDataResponse): Promise<void> {
		if (!this.regionofferinglinks) {
			this.setRegionOfferingLinks([]);
		}
		if (!this.offeringframes) {
			this.setOfferingFrames([]);
		}
		if (!this.offeringframetemplates) {
			this.setOfferingFrameTemplates([]);
		}
		if (!this.offeringframeimages) {
			this.setOfferingFrameImages([]);
		}
		if (!this.offeringofferingframelinks) {
			this.setOfferingOfferingFrameLinks([]);
		}
		if (!this.offerings) {
			this.setOfferings([]);
		}
		if (!this.offeringoptions) {
			this.setOfferingOptions([]);
		}
		if (!this.offeringoptionvalues) {
			this.setOfferingOptionValues([]);
		}
		if (!this.offeringoptionvalueofferinglinks) {
			this.setOfferingOptionValueOfferingLinks([]);
		}
		if (!this.editorMessages) {
			this.setEditorMessages([]);
		}
		if (!this.offeringpages) {
			this.setOfferingPages([]);
		}
		if (!this.pricing) {
			this.setPricing([]);
		}
		if (!this.models2d) {
			this.setModels2D([]);
		}
		if (!this.models3d) {
			this.setModels3D([]);
		}
		if (!this.flexgroups) {
			this.setFlexgroups([]);
		}

		const promises: any[] = [];

		if (responseData.region_offerings?.length) {
			promises.push(this.setRegionOfferingLinks(
				mergeAndMergeByKey(
					this.regionofferinglinks,
					_.toArray<DB.RegionOfferingModel[]>(responseData.region_offerings),
					'id',
				),
			));
		}
		if (responseData.offeringframes?.length) {
			promises.push(this.setOfferingFrames(
				mergeAndMergeByKey(
					this.offeringframes,
					_.toArray<DB.OfferingFrameModel[]>(responseData.offeringframes),
					'id',
				),
			));
		}
		if (responseData.offeringframeimages?.length) {
			promises.push(this.setOfferingFrameImages(
				mergeAndMergeByKey(
					this.offeringframeimages,
					_.toArray<DB.OfferingFrameImageModel[]>(responseData.offeringframeimages),
					'id',
				),
			));
		}
		if (responseData.maskimages?.length) {
			promises.push(this.setOfferingMaskImages(
				mergeAndMergeByKey(
					this.offeringmaskimages,
					_.toArray<DB.OfferingMaskImageModel[]>(responseData.maskimages),
					'id',
				),
			));
		}
		if (responseData.overlayimages?.length) {
			promises.push(this.setOfferingOverlayImages(
				mergeAndMergeByKey(
					this.offeringoverlayimages,
					_.toArray<DB.OfferingOverlayImageModel[]>(responseData.overlayimages),
					'id',
				),
			));
		}
		if (responseData.offeringframetemplates?.length) {
			promises.push(this.setOfferingFrameTemplates(
				mergeAndMergeByKey(
					this.offeringframetemplates,
					_.toArray<DB.OfferingFrameTemplateModel[]>(responseData.offeringframetemplates),
					'id',
				),
			));
		}
		if (responseData.offering_offeringframes?.length) {
			promises.push(this.setOfferingOfferingFrameLinks(
				mergeAndMergeByKey(
					this.offeringofferingframelinks,
					_.toArray<DB.OfferingOfferingFrameLinkModel[]>(responseData.offering_offeringframes),
					'id',
				),
			));
		}
		if (responseData.offerings?.length) {
			promises.push(this.setOfferings(
				mergeAndMergeByKey(
					this.offerings,
					_.toArray<DB.OfferingModel[]>(responseData.offerings),
					'id',
				),
			));
		}
		if (responseData.offeringoptions?.length) {
			promises.push(this.setOfferingOptions(
				mergeAndMergeByKey(
					this.offeringoptions,
					responseData.offeringoptions as DB.OfferingOptionModel[],
					'id',
				),
			));
		}
		if (responseData.offeringoptionvalues?.length) {
			promises.push(this.setOfferingOptionValues(
				mergeAndMergeByKey(
					this.offeringoptionvalues,
					responseData.offeringoptionvalues as DB.OfferingOptionValueModel[],
					'id',
				),
			));
		}
		if (responseData.offeringoptionvalue_offerings?.length) {
			promises.push(this.setOfferingOptionValueOfferingLinks(
				mergeAndMergeByKey(
					this.offeringoptionvalueofferinglinks,
					responseData.offeringoptionvalue_offerings as DB.OfferingOptionValueOfferingModel[],
					'id',
				),
			));
		}
		if (responseData.editorMessages?.length) {
			promises.push(this.setEditorMessages(
				mergeAndMergeByKey(
					this.editorMessages,
					responseData.editorMessages,
					'id',
				),
			));
		}
		if (responseData.pages?.length) {
			promises.push(this.setOfferingPages(
				mergeOfferingPageSpecifications(
					this.offeringpages,
					responseData.pages,
				),
			));
		}
		if (responseData.flexgroups?.length) {
			promises.push(this.setFlexgroups(
				mergeAndMergeByKey(
					this.flexgroups,
					responseData.flexgroups as API.FlexGroupModel[],
					'id',
				),
			));
		}
		if (responseData.pricing?.length) {
			promises.push(this.setPricing(
				mergeAndMergeByKey(
					this.pricing,
					responseData.pricing as DB.PricingModel[],
					'id',
				),
			));
		}
		if (responseData.model2ds?.length) {
			promises.push(this.setModels2D(
				mergeAndMergeByKey(
					this.models2d,
					responseData.model2ds as DB.Model2DModel[],
					'id',
				),
			));
		}
		if (responseData.model3ds?.length) {
			promises.push(this.setModels3D(
				mergeAndMergeByKey(
					this.models3d,
					responseData.model3ds as DB.Model3DModel[],
					'id',
				),
			));
		}

		promises.push(this.setTranslations(responseData.translations));

		return Promise.all(promises).then(() => undefined);
	}

	@Action({
		rawError: true,
	})
	public async fetchOfferingsData({
		requestOptions,
		methodOptions,
		offeringIds,
		searchProps,
	}: {
		requestOptions?: AxiosRequestConfig;
		methodOptions?: AjaxOptions;
		offeringIds?: number[];
		searchProps?: OfferingModelSearchProps & { get_from_flexgroupid?: boolean };
	}): Promise<void> {
		if (window.glPlatform === 'server') {
			return undefined;
		}

		const defaultRequestOptions: AxiosRequestConfig = {
			method: 'get',
			url: `${window.glAppUrl}api/app/offeringsdata`,
		};
		const defaultMethodOptions: AjaxOptions = {
			auth: false,
			debug: {
				dialog: true,
			},
		};

		if (
			typeof offeringIds === 'undefined'
			&& typeof searchProps === 'undefined'
		) {
			throw new Error('You must provide either `offeringIds` or `searchProps` to fetch the offerings data');
		}

		requestOptions = (
			requestOptions
				? merge(
					defaultRequestOptions,
					requestOptions,
				)
				: defaultRequestOptions
		);
		methodOptions = (
			methodOptions
				? merge(
					defaultMethodOptions,
					methodOptions,
				)
				: defaultMethodOptions
		);

		if (requestOptions.url) {
			const url = new URL(requestOptions.url);

			if (window.glStack !== 'live') {
				// We always want to get the latest data from the server when in dev/test/staging mode
				url.searchParams.set(
					'version',
					(new Date().getTime() / 1000).toString(),
				);
			} else if (window.appDataVersion) {
				// In production we want to make use of cloudFront caching, so we need a version number
				url.searchParams.set(
					'version',
					window.appDataVersion,
				);
			}

			if (offeringIds) {
				url.searchParams.set(
					'ids',
					offeringIds.join(','),
				);
			} else if (searchProps) {
				if (typeof searchProps.get_from_flexgroupid !== 'undefined') {
					url.searchParams.set(
						'get_from_flexgroupid',
						searchProps.get_from_flexgroupid.toString(),
					);
					delete searchProps.get_from_flexgroupid;
				}

				const searchPropsKeys = Object.keys(searchProps) as (keyof typeof searchProps)[];
				searchPropsKeys.sort();
				const searchParam = searchPropsKeys.reduce(
					(object, key) => ({
						...object,
						[key]: searchProps[key],
					}),
					{},
				);
				url.searchParams.set(
					'where',
					JSON.stringify(searchParam),
				);
			}

			const shopId = await UserModule.getShopId();

			if (shopId) {
				url.searchParams.set(
					'shopid',
					shopId.toString(),
				);
			}

			requestOptions.url = url.toString();
		}

		return ajax
			.request(
				requestOptions,
				methodOptions,
			)
			.then((response) => this.queueOfferingsDataMerge(response.data as API.OfferingsDataResponse))
			.then(() => undefined);
	}

	@Action({
		rawError: true,
	})
	public fetch({
		requestOptions,
		methodOptions,
		dataToExclude,
	}: {
		requestOptions?: AxiosRequestConfig;
		methodOptions?: AjaxOptions;
		dataToExclude?: StartAppDataToExclude[];
	} = {}): Promise<void> {
		const defaultRequestOptions: AxiosRequestConfig = {
			method: 'get',
			url: this.collectionUrl,
		};
		const defaultMethodOptions: AjaxOptions = {
			auth: false,
			debug: {
				dialog: true,
			},
		};

		requestOptions = (
			requestOptions
				? merge(
					defaultRequestOptions,
					requestOptions,
				)
				: defaultRequestOptions
		);
		methodOptions = (
			methodOptions
				? merge(
					defaultMethodOptions,
					methodOptions,
				)
				: defaultMethodOptions
		);

		if (
			dataToExclude?.length
			&& requestOptions.url
		) {
			const url = new URL(requestOptions.url);
			url.searchParams.set(
				'exclude',
				dataToExclude.join(','),
			);

			if (UserModule.countryid) {
				url.searchParams.set(
					'countryid',
					UserModule.countryid.toString(),
				);
			}

			if (UserModule.currency) {
				url.searchParams.set(
					'usercurrency',
					UserModule.currency,
				);
			}

			if (UserModule.language) {
				url.searchParams.set(
					'userlanguage',
					UserModule.language,
				);
			}

			requestOptions.url = url.toString();
		}

		return ajax
			.request(
				requestOptions,
				methodOptions,
			)
			.then((response) => {
				const responseData = response.data;

				ConfigModule.addModels(responseData.settings);

				if (responseData.languages) {
					this.setLanguages(_.toArray(responseData.languages));
				}
				if (responseData.countries) {
					this.setCountries(_.toArray(responseData.countries));
				}
				if (responseData.stateprovs) {
					this.setStateProvs(_.toArray(responseData.stateprovs));
				}
				if (responseData.pricing) {
					this.setPricing(_.toArray(responseData.pricing));
				}
				if (responseData.photosources) {
					ChannelsModule.addModels(_.toArray(responseData.photosources));
				}
				if (responseData.productgroups) {
					this.setProductGroups(_.toArray(responseData.productgroups));
				}
				if (responseData.regions) {
					this.setRegions(_.toArray(responseData.regions));
				}
				if (responseData.region_offerings) {
					this.setRegionOfferingLinks(_.toArray(responseData.region_offerings));
				}
				if (responseData.upsell) {
					this.setUpsells(_.toArray(responseData.upsell));
				}
				if (responseData.flexgroups) {
					this.setFlexgroups(responseData.flexgroups);
				}
				if (responseData.offeringframes) {
					this.setOfferingFrames(_.toArray(responseData.offeringframes));
				}
				if (responseData.maskimages) {
					this.setOfferingMaskImages(_.toArray(responseData.maskimages));
				}
				if (responseData.overlayimages) {
					this.setOfferingOverlayImages(_.toArray(responseData.overlayimages));
				}
				if (responseData.productcategories) {
					this.setProductCategories(_.toArray(responseData.productcategories));
				}
				if (responseData.handling) {
					this.setHandling(_.toArray(responseData.handling));
				}

				this.setBulks(_.toArray(responseData.bulk));
				this.setBulkQuantities(_.toArray(responseData.bulkquantity));
				this.setBulkProductTypes(_.toArray(responseData.bulkproducttypes));

				if (responseData.buckets) {
					this.setBuckets(_.toArray(responseData.buckets));
				}
				if (responseData.currencies) {
					this.setCurrencies(_.toArray(responseData.currencies));
				}
				if (responseData.region_currencies) {
					this.setRegionCurrencyLinks(_.toArray(responseData.region_currencies));
				}
				if (responseData.offerings) {
					this.setOfferings(_.toArray(responseData.offerings));
				}
				if (responseData.offeringoptions) {
					this.setOfferingOptions(responseData.offeringoptions);
				}
				if (responseData.offeringoptionvalues) {
					this.setOfferingOptionValues(responseData.offeringoptionvalues);
				}
				if (responseData.offeringoptionvalue_offerings) {
					this.setOfferingOptionValueOfferingLinks(responseData.offeringoptionvalue_offerings);
				}
				if (responseData.editorMessages) {
					this.setEditorMessages(responseData.editorMessages);
				}
				if (responseData.pages) {
					this.setOfferingPages(responseData.pages);
				}
				if (responseData.model2ds) {
					this.setModels2D(responseData.model2ds);
				}
				if (responseData.model3ds) {
					this.setModels3D(responseData.model3ds);
				}
				if (responseData.hyperlinks) {
					this.setHyperlinks(_.toArray(responseData.hyperlinks));
				}
				if (responseData.pdps) {
					this.setPDPs(responseData.pdps);
				}
				if (responseData.pdpfilters) {
					this.setPDPFilters(responseData.pdpfilters);
				}
				if (responseData.pdpfiltervalues) {
					this.setPDPFilterValues(responseData.pdpfiltervalues);
				}
				if (responseData.pdpfiltervalue_offerings) {
					this.setPDPFilterValueOfferingLinks(responseData.pdpfiltervalue_offerings);
				}
				if (responseData.pdpimages) {
					this.setPDPImages(responseData.pdpimages);
				}

				this.setBadges(responseData.badges);
				this.setBadgeImages(responseData.badgeimages);
				this.setBadgeOfferingLinks(responseData.badge_offerings);
				this.setBadgePDPLinks(responseData.badge_pdp);
				this.setBadgeProductCategoryLinks(responseData.badge_productcategory);

				if (responseData.fonts) {
					// Fonts need to be parsed after the buckets, as they need the url path
					FontModule
						.addModels(_.toArray(responseData.fonts))
						.catch(() => {
							// Swallow error: no action required
						});
				}

				/*
				 * Apply per-label brand styling (FE-2372). White-label partners
				 * on the default build can override editor-chrome design tokens
				 * and UI fonts at runtime; absent/invalid data is a safe no-op.
				 * Runs after buckets are set, since custom font URLs resolve
				 * against the webfonts bucket (same reason as the fonts above).
				 */
				applyBrandStyles(
					responseData.brandStyles,
					{
						loadFonts: (fonts) => FontModule.addModels(fonts),
					},
				);

				if (responseData.translations) {
					responseData.translations.forEach((translationRecord: DB.TranslationModel) => {
						window.App.router.$i18next.addResourceBundle(
							translationRecord.language,
							translationRecord.namespace,
							translationRecord.bundle,
						);
					});
				}

				/*
				 * Load the Webfont to display the Material Icons used in the interface of this app
				 * Fire-and-forget: blocking here would chain the font CDN's latency (and outages)
				 * into the app data fetch that gates the launch sequence
				 */
				FontModule
					.addModels([
						{
							id: 'Material Icons',
							provider: 'google',
							print: false,
							autoload: true,
						},
						{
							id: 'Material Icons Round',
							provider: 'google',
							print: false,
							autoload: true,
						},
						{
							id: 'Material Icons Outlined',
							provider: 'google',
							print: false,
							autoload: true,
						},
					])
					.catch((e) => {
						/* Icons swap in when the font eventually loads; report so CDN outages stay visible */
						reportError(
							e,
							'warning',
						);
					});

				return undefined;
			});
	}

	@Action({
		rawError: true,
	})
	public getAndSetOfferingFrameImage(offeringframeimage: DB.OfferingFrameImageModel): void {
		const offeringFrameImageFound = this._offeringframesimages[offeringframeimage.id];

		if (!offeringFrameImageFound) {
			const image = new Image();

			/*
			 * This enumerated attribute indicates if the fetching of the related image must be done using CORS or not.
			 * CORS-enabled images can be reused in the <canvas> element without being tainted.
			 */
			image.crossOrigin = 'Anonymous';

			image.onload = () => {
				if (
					!offeringframeimage.height
					|| !offeringframeimage.width
				) {
					this.setOfferingFrameImageDimensions({
						id: offeringframeimage.id,
						height: image.naturalHeight,
						width: image.naturalWidth,
					});
				}

				this.setOfferingFrameImage({
					offeringframeimageid: offeringframeimage.id,
					image,
				});
			};
			image.src = offeringframeimage.url;
		}
	}

	@Action({
		rawError: true,
	})
	public getAndSetOfferingMaskImage(
		payload: OfferingPageAssetPayload,
	): void {
		const source = resolveOfferingMaskAssetSourceFromPayload(
			this.offeringmaskimages,
			payload,
		);

		if (
			!source
			|| this._offeringmasksimages[source.cacheKey]
		) {
			return;
		}

		const image = new Image();

		/*
		 * This enumerated attribute indicates if the fetching of the related image must be done using CORS or not.
		 * CORS-enabled images can be reused in the <canvas> element without being tainted.
		 */
		image.crossOrigin = 'Anonymous';

		image.onload = () => {
			this.setOfferingMaskImage({
				cacheKey: source.cacheKey,
				image,
			});
		};
		image.src = source.url;
	}

	@Action({
		rawError: true,
	})
	public getAndSetOfferingOverlayImage(
		payload: OfferingPageAssetPayload,
	): void {
		const source = resolveOfferingOverlayAssetSourceFromPayload(
			this.offeringoverlayimages,
			payload,
		);

		if (
			!source
			|| this._offeringoverlaysimages[source.cacheKey]
		) {
			return;
		}

		const image = new Image();

		/*
		 * This enumerated attribute indicates if the fetching of the related image must be done using CORS or not.
		 * CORS-enabled images can be reused in the <canvas> element without being tainted.
		 */
		image.crossOrigin = 'Anonymous';

		image.onload = () => {
			this.setOfferingOverlayImage({
				cacheKey: source.cacheKey,
				image,
			});
		};
		image.src = source.url;
	}

	@Action({
		rawError: true,
	})
	public setOfferingFrameImage(
		payload: {
			offeringframeimageid: DB.OfferingFrameImageModel['id'],
			image: HTMLImageElement,
		},
	) {
		this._setOfferingFrameImage(payload);
	}

	@Action({
		rawError: true,
	})
	public setOfferingFrameImageDimensions(data: Pick<DB.OfferingFrameImageModel, 'id' | 'height' | 'width'>): void {
		this._setOfferingFrameImageDimensions(data);
	}

	@Mutation
	private _setOfferingFrameImageDimensions(data: Pick<DB.OfferingFrameImageModel, 'id' | 'height' | 'width'>): void {
		const offeringFrameImage = this.offeringframeimages.find((offeringframeimage) => offeringframeimage.id === data.id);

		if (offeringFrameImage) {
			Object.assign(
				offeringFrameImage,
				{
					height: data.height,
					width: data.width,
				},
			);
		}
	}

	@Action({
		rawError: true,
	})
	public setOfferingMaskImage(
		payload: {
			cacheKey: string,
			image: HTMLImageElement,
		},
	) {
		this._setOfferingMaskImage(payload);
	}

	@Action({
		rawError: true,
	})
	public setOfferingOverlayImage(
		payload: {
			cacheKey: string,
			image: HTMLImageElement,
		},
	) {
		this._setOfferingOverlayImage(payload);
	}

	@Action({
		rawError: true,
	})
	public unsetOfferingFrameImage(offeringframe: OfferingFrameModel): void {
		const offeringFrameImageFound = this._offeringframesimages[offeringframe.id];

		if (offeringFrameImageFound) {
			this._unsetOfferingFrameImage(offeringframe.id);
		}
	}

	@Action({
		rawError: true,
	})
	public unsetOfferingMaskImage(
		payload: OfferingPageAssetPayload,
	): void {
		const source = resolveOfferingMaskAssetSourceFromPayload(
			this.offeringmaskimages,
			payload,
		);

		if (
			source
			&& this._offeringmasksimages[source.cacheKey]
		) {
			this._unsetOfferingMaskImage(source.cacheKey);
		}
	}

	@Action({
		rawError: true,
	})
	public unsetOfferingOverlayImage(
		payload: OfferingPageAssetPayload,
	): void {
		const source = resolveOfferingOverlayAssetSourceFromPayload(
			this.offeringoverlayimages,
			payload,
		);

		if (
			source
			&& this._offeringoverlaysimages[source.cacheKey]
		) {
			this._unsetOfferingOverlayImage(source.cacheKey);
		}
	}
}
